import { prevPeriod, deltaText } from "@/lib/prevPeriod";
import { useSharedKpiFilters } from "@/lib/kpiFilterMemory";
import { CreateReportButton } from "@/components/CreateReportButton";
import { PageActions, scrollToAiBlock } from "@/components/PageActions";
import { LastImportInfo } from "@/components/LastImportInfo";
import AchatsInpulse from "./achats/analyse/AnalyseInpulse";
import { Sparkles } from "lucide-react";
import { useEffect, useMemo, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toParams } from "@/lib/kpi";
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  ComposedChart, XAxis, YAxis, ResponsiveContainer, Tooltip,
  CartesianGrid, ReferenceLine, Legend,
} from "recharts";
import {
  ShoppingCart, Download, Upload, Calculator, ChevronDown,
  TrendingUp, TrendingDown, AlertTriangle, CheckCircle2, Search, X as XIcon,
  Percent, Fish, Package, Truck, Trophy, Target, Info, Users, ArrowRight,
  BarChart3, Layers, LayoutGrid, Store, GitCompare,
} from "lucide-react";
import { toast } from "sonner";
import { KpiTableToolbar, SortHeadCell } from "@/components/kpi/KpiTableToolbar";

import InventairesPage from "./achats/inventaires/Inventaires";
import ComparateurTab from "./achats/ComparateurTab";
import FilterMultiSelect, { SearchSelect } from "@/components/filters/FilterMultiSelect";

import { OrbitAI } from "@/components/OrbitAI";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";
const MONTHS_FR = ["JANVIER","FEVRIER","MARS","AVRIL","MAI","JUIN","JUILLET","AOUT","SEPTEMBRE","OCTOBRE","NOVEMBRE","DECEMBRE"];
const FOUR_COLORS = { "TERRE AZUR":"#2563EB","PEDRERO":"#10B981","PEDRO":"#10B981","BACK EUROP":"#F59E0B","BACK EUROPE":"#F59E0B","REUNIMER":"#8B5CF6","SML":"#EF4444","NOVEO":"#EC4899","LNS TRADE":"#0EA5E9","MBA GREEN":"#84CC16","Autres":"#94A3B8" };

const fmtEur = v => (v==null||isNaN(v))?"—":Math.round(v).toLocaleString("fr-FR").replace(/,/g," ")+" €";
const fmtEurShort = v => { if (v==null||isNaN(v)) return "—"; return Math.round(v).toLocaleString("fr-FR").replace(/\u202F/g," ").replace(/,/g," ")+" €"; };
const fmtPct = (v,d=1) => (v==null||isNaN(v))?"—":`${(v*100).toFixed(d).replace(".",",")}%`;
const fmtNum = (v,d=0) => (v==null||isNaN(v))?"—":v.toLocaleString("fr-FR",{maximumFractionDigits:d,minimumFractionDigits:d});
const fmtPts = v => (v==null||isNaN(v))?"—":`${v>=0?"+":""}${(v*100).toFixed(1).replace(".",",")} pts`;

/* ─── Multi-select ─── */
function MultiSelect({ label, options, values, onChange, testid, single=false, labelMap }) {
  return <FilterMultiSelect label={label} options={options} labelMap={labelMap} value={values||[]} onChange={onChange} testid={testid} single={single} size="md" topLabel className="flex-1 min-w-[140px]" placeholder={`Tous les ${label.toLowerCase()}`}/>;
}

/* ─── KPI Card ─── */
// Variation réelle vs période de comparaison (null → aucune mention, jamais de valeur inventée)
function deltaSub(cur, prev, label, { pt = false, goodWhenDown = false } = {}) {
  const d = deltaText(cur, prev, { pt });
  if (!d || !label) return {};
  const good = goodWhenDown ? d.value <= 0 : d.value >= 0;
  return { sub: `${d.text} ${label}`, subTone: d.value === 0 ? undefined : good ? "up" : "down" };
}

function KpiCard({ icon:Icon, iconBg, iconColor, label, value, sub, subTone, badge, progress, testid }) {
  const toneCls = subTone==="up"?"text-emerald-600 font-semibold":subTone==="down"?"text-rose-600 font-semibold":"text-slate-500";
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 flex flex-col gap-1.5 shadow-[0_1px_3px_rgba(15,23,42,.04)]" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5"><span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">{label}</span>{badge && <span className="text-[8px] font-bold px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700">{badge}</span>}</div>
        {Icon && <div className={`w-7 h-7 rounded-md ${iconBg} grid place-items-center`}><Icon className={`w-3.5 h-3.5 ${iconColor}`}/></div>}
      </div>
      <div className="text-[22px] font-bold text-slate-900 leading-none tabular-nums">{value}</div>
      {progress != null && (
        <div className="w-full h-1.5 rounded-full bg-slate-100 overflow-hidden">
          <div className={`h-full rounded-full ${progress<0.85?"bg-emerald-500":progress<1?"bg-amber-500":"bg-rose-500"}`} style={{width:`${Math.min(100,progress*100)}%`}}/>
        </div>
      )}
      {sub && <div className={`text-[11px] ${toneCls}`}>{sub}</div>}
    </div>
  );
}

/* ─── Gauge (semi-circular) ─── */
function Gauge({ value, target, min=0.20, max=0.28, label }) {
  const pct = Math.min(1, Math.max(0, (value - min) / (max - min)));
  const angle = -90 + pct * 180;
  const ok = value <= target + 0.005;
  return (
    <div className="relative w-full max-w-[260px] mx-auto">
      <svg viewBox="0 0 200 120" className="w-full">
        <defs>
          <linearGradient id="gA" x1="0" x2="1"><stop offset="0" stopColor="#10B981"/><stop offset=".5" stopColor="#F59E0B"/><stop offset="1" stopColor="#EF4444"/></linearGradient>
        </defs>
        <path d="M 20 100 A 80 80 0 0 1 180 100" stroke="url(#gA)" strokeWidth="18" fill="none" strokeLinecap="round"/>
        <line x1="100" y1="100" x2={100 + 68*Math.cos(angle*Math.PI/180)} y2={100 + 68*Math.sin(angle*Math.PI/180)} stroke="#0F172A" strokeWidth="3" strokeLinecap="round"/>
        <circle cx="100" cy="100" r="5" fill="#0F172A"/>
      </svg>
      <div className="text-center -mt-6">
        <div className="text-2xl font-bold text-slate-900 tabular-nums">{fmtPct(value)}</div>
        <div className="text-[11px] text-slate-500">{label || "Objectif"} : {fmtPct(target)}</div>
      </div>
      <div className="flex items-center justify-between text-[10px] text-slate-400 mt-1 px-2 tabular-nums">
        <span>{fmtPct(min,0)}</span><span>{fmtPct(target,0)}</span><span>{fmtPct(max,0)}</span>
      </div>
      <div className={`mt-3 text-center rounded-lg px-3 py-2 text-[11px] font-semibold ${ok?"bg-emerald-50 text-emerald-700":"bg-rose-50 text-rose-700"}`}>
        {ok ? "Vous êtes dans l'objectif" : `Vous êtes ${((value-target)*100).toFixed(1).replace(".",",")} pts au-dessus`}
      </div>
    </div>
  );
}

/* ═════════════════════════════════════════════════════════════════ */
export default function KpiAchats() {
  const [tab, setTab] = useState("synth");
  const [meta, setMeta] = useState(null);
  const [f, setF] = useSharedKpiFilters({ annees: [2026] }, { meta });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [search, setSearch] = useState("");

  useEffect(() => { api.get("/kpi/achat/tracking-meta").then(r=>setMeta(r.data)).catch(()=>{}); }, []);
  const load = useCallback(() => {
    setLoading(true);
    const pp = prevPeriod(f);
    const base = { ...f }; delete base.date_from; delete base.date_to; delete base.mois; delete base.annees;
    const prevReq = pp
      ? api.get("/kpi/achat/v2/summary", { params: { ...toParams(base), ...pp.params } }).then(r => r.data?.summary || null).catch(() => null)
      : Promise.resolve(null);
    Promise.all([api.get("/kpi/achat/v2/summary", { params: toParams(f) }), prevReq])
      .then(([r, prevSummary]) => setData({ ...r.data, prev_summary: prevSummary, prev_label: pp?.label || null }))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [f]);
  useEffect(load, [load]);

  const subtitles = {
    synth: "Pilotage des achats · FoodCost · Fournisseurs · Budgets",
    detail: "Pilotage des achats · FoodCost · Fournisseurs · Budgets",
    four: "Pilotage des achats · FoodCost · Fournisseurs · Budgets",
    simu: "Simulateur FoodCost · Anticipez vos achats · Prenez les bonnes décisions",
    cmp: "Comparez vos performances avec des structures d'activité similaire",
  };
  const tabDefs = [
    { k:"synth",  label:"Vue synthèse",     icon:LayoutGrid },
    { k:"detail", label:"Vue détaillée",    icon:Layers },
    { k:"four",   label:"Fournisseurs",     icon:Truck },
    { k:"inv",    label:"Inventaires",      icon:Package },
    { k:"simu",   label:"Simulateur FoodCost", icon:Calculator },
    { k:"cmp",    label:"Comparateur",       icon:GitCompare },
    { k:"inpulse", label:"Analyse Inpulse",  icon:Sparkles },
  ];

  const doImport = useCallback(async (endpoint, file) => {
    if (!file) return; setImportOpen(false);
    const fd = new FormData(); fd.append("file", file);
    try {
      const r = await api.post(endpoint, fd, { headers:{"Content-Type":"multipart/form-data"} });
      toast.success(r.data.message || "Import réussi");
      load();
    } catch (e) { toast.error("Import échoué : " + (e.response?.data?.detail || e.message)); }
  }, [load]);

  const onExport = useCallback(() => {
    if (!data) return;
    const rows = data.by_corner || [];
    const headers = ["Corner","DR","Secteur","BM","CA HT","Budget achat","Achats HT","Reste","FoodCost","Objectif","Écart pts","Casse %","Kg saumon","Fournisseur principal"];
    const lines = [headers.join(";")].concat(rows.map(r=>[
      r.corner, r.dr||"", r.secteur||"", r.bm||"",
      Math.round(r.ca_ht||0), Math.round(r.budget||0), Math.round(r.achats_ht||0), Math.round(r.reste_budget||0),
      ((r.foodcost||0)*100).toFixed(1).replace(".",","),
      ((r.obj_foodcost||0)*100).toFixed(1).replace(".",","),
      ((r.ecart_pts||0)*100).toFixed(1).replace(".",","),
      ((r.casse_pct||0)*100).toFixed(1).replace(".",","),
      Math.round(r.kg_saumon||0),
      r.fournisseur_principal||"—",
    ].map(v=>`"${String(v).replace(/"/g,'""')}"`).join(";")));
    const blob = new Blob(["\uFEFF"+lines.join("\n")],{type:"text/csv;charset=utf-8;"});
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href=url;
    a.download = `orbit_achat_${tab}_${new Date().toISOString().slice(0,10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  }, [data, tab]);

  return (
    <div className="space-y-4" data-testid="kpi-achat-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-blue-600 text-white grid place-items-center font-bold"><ShoppingCart className="w-5 h-5"/></div>
          <div>
            <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900 uppercase leading-none">Achat</h1>
            <p className="text-[11px] text-slate-500 mt-0.5">{subtitles[tab]}</p>
            <LastImportInfo kinds={["achats", "mix_fournisseur", "inventaire"]} className="mt-1.5"/><CreateReportButton module="achats" className="mt-2 ml-2"/>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={()=>setTab("simu")} className="flex items-center gap-1.5 text-[11px] font-semibold px-3 py-1.5 rounded-lg bg-slate-900 hover:bg-slate-800 text-white" data-testid="ach-simu-btn">
            <Calculator className="w-3.5 h-3.5"/> Simulateur FoodCost
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-2 border-b border-slate-200 flex-wrap">
        {tabDefs.map(t => {
          const Icon = t.icon; const active = tab===t.k;
          return (
            <button key={t.k} data-testid={`ach-tab-${t.k}`} onClick={()=>setTab(t.k)}
                    className={`flex items-center gap-1.5 px-4 py-2 text-[13px] font-semibold border-b-2 transition ${active?"border-blue-600 text-blue-600":"border-transparent text-slate-500 hover:text-slate-700"}`}>
              <Icon className="w-3.5 h-3.5"/> {t.label}
            </button>
          );
        })}
        <PageActions title="Achat" filters={() => document.querySelector("[data-testid=ach-filters]")?.innerText?.replace(/\s+/g, " ").slice(0, 160) || ""} onAi={scrollToAiBlock} aiLabel="Analyser avec l'IA" onExcel={onExport} testid="ach-actions" />
      </div>

      {/* Filter bar (masquée sur Inventaires — filtres indépendants) */}
      {tab !== "simu" && tab !== "inv" && tab !== "cmp" && <FilterBar meta={meta} f={f} setF={setF}/>}

      {loading && tab !== "inv" && tab !== "cmp" && <div className="bg-white rounded-xl border border-slate-200 p-8 text-center text-sm text-slate-400">Chargement…</div>}
      {!loading && data && tab==="synth"  && <TabSynthese data={data} f={f} search={search} setSearch={setSearch}/>}
      {!loading && data && tab==="detail" && <TabDetail   data={data} search={search} setSearch={setSearch}/>}
      {!loading && data && tab==="four"   && <TabFour     data={data} search={search} setSearch={setSearch}/>}
      {tab==="inv"  && <InventairesPage/>}
      {tab==="simu" && <TabSimu meta={meta} f={f} setF={setF} data={data}/>}
      {tab==="cmp"  && <ComparateurTab/>}
      {tab==="inpulse" && <AchatsInpulse f={f}/>}
    </div>
  );
}

/* ─── Drawer détail semaine (CA/Achats/Casse journalier) ─── */
function WeekDetailDrawer({ semaine, f, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (semaine == null) return;
    const p = new URLSearchParams(toParams(f || {}));
    p.append("semaine", String(semaine));
    api.get(`/kpi/achats/week-detail?${p.toString()}`)
       .then(r => setData(r.data))
       .catch(()=>setData({week_totals:{}, days:[], note:""}));
  }, [semaine, f]);
  const w = data?.week_totals || {};
  const days = data?.days || [];
  return (
    <>
      <div className="fixed inset-0 z-40 bg-slate-900/40" onClick={onClose}/>
      <div className="fixed right-0 top-0 h-full w-[720px] max-w-full bg-white z-50 shadow-2xl overflow-y-auto p-5" data-testid="week-detail-drawer">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Détail semaine</div>
            <div className="text-lg font-bold text-slate-900">Semaine {String(semaine).padStart(2,"0")}</div>
            <div className="text-[11px] text-slate-500">{days[0]?.date} → {days[days.length-1]?.date} · {w.n_corners||0} corners</div>
          </div>
          <button onClick={onClose} data-testid="week-drawer-close" className="p-1 rounded hover:bg-slate-100"><XIcon className="w-5 h-5"/></button>
        </div>

        {/* Cards synthèse hebdo */}
        <div className="grid grid-cols-4 gap-2 mb-4">
          <div className="bg-slate-50 rounded-lg p-2.5">
            <div className="text-[9px] font-semibold uppercase tracking-wider text-slate-500">CA HT</div>
            <div className="text-[16px] font-bold text-slate-900 tabular-nums">{fmtEur(w.ca_ht)}</div>
          </div>
          <div className="bg-blue-50 rounded-lg p-2.5">
            <div className="text-[9px] font-semibold uppercase tracking-wider text-slate-500">Achats</div>
            <div className="text-[16px] font-bold text-blue-700 tabular-nums">{fmtEur(w.achats)}</div>
          </div>
          <div className="bg-rose-50 rounded-lg p-2.5">
            <div className="text-[9px] font-semibold uppercase tracking-wider text-slate-500">Casse</div>
            <div className="text-[16px] font-bold text-rose-700 tabular-nums">{fmtEur(w.casse_eur)}</div>
            <div className="text-[9px] text-rose-600">{fmtPct(w.casse_pct,2)} du CA</div>
          </div>
          <div className={`rounded-lg p-2.5 ${w.foodcost<=w.obj_foodcost?"bg-emerald-50":"bg-amber-50"}`}>
            <div className="text-[9px] font-semibold uppercase tracking-wider text-slate-500">FoodCost</div>
            <div className={`text-[16px] font-bold tabular-nums ${w.foodcost<=w.obj_foodcost?"text-emerald-700":"text-amber-700"}`}>{fmtPct(w.foodcost)}</div>
            <div className="text-[9px] text-slate-500">Obj. {fmtPct(w.obj_foodcost,0)}</div>
          </div>
        </div>

        {/* Chart journalier */}
        {days.length > 0 && (
          <div className="border border-slate-200 rounded-lg p-3 mb-3">
            <div className="text-[11px] font-bold text-slate-800 uppercase tracking-wider mb-2">Évolution journalière</div>
            <ResponsiveContainer width="100%" height={220}>
              <ComposedChart data={days.map(d => ({...d, label: `${d.jour_sem||""} ${d.date?.slice(-5) || ""}`}))}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0"/>
                <XAxis dataKey="label" tick={{fontSize:9}} axisLine={false}/>
                <YAxis yAxisId="l" tick={{fontSize:9}} tickFormatter={v=>Math.round(v).toLocaleString("fr-FR").replace(/,/g," ")}/>
                <Tooltip formatter={v => fmtEur(v)}/>
                <Legend wrapperStyle={{fontSize:10}}/>
                <Bar yAxisId="l" dataKey="ca_ht" name="CA HT" fill="#2563EB" opacity={0.85}/>
                <Bar yAxisId="l" dataKey="achats_est" name="Achats (est.)" fill="#F59E0B" opacity={0.75}/>
                <Line yAxisId="l" type="monotone" dataKey="casse_est" name="Casse (est.)" stroke="#EF4444" strokeWidth={2} dot={{r:2}}/>
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Table journalière */}
        <div className="overflow-x-auto border border-slate-200 rounded-lg">
          <table className="min-w-[640px] w-full text-[11px]">
            <thead className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-3 py-2 text-left">Jour</th>
                <th className="px-3 py-2 text-right">CA HT</th>
                <th className="px-3 py-2 text-right">Objectif</th>
                <th className="px-3 py-2 text-right">Écart obj.</th>
                <th className="px-3 py-2 text-right">N-1</th>
                <th className="px-3 py-2 text-right">Achats est.</th>
                <th className="px-3 py-2 text-right">Casse est.</th>
                <th className="px-3 py-2 text-right">FC est.</th>
              </tr>
            </thead>
            <tbody>
              {days.map((d, i) => (
                <tr key={i} data-testid={`week-day-${i}`} className="border-b border-slate-100">
                  <td className="px-3 py-1.5 font-semibold text-slate-800">{d.jour_sem} <span className="text-slate-400 font-normal">{d.date?.slice(-5)}</span></td>
                  <td className="px-3 py-1.5 text-right tabular-nums font-semibold">{fmtEur(d.ca_ht)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-slate-500">{fmtEur(d.objectif)}</td>
                  <td className={`px-3 py-1.5 text-right tabular-nums font-semibold ${(d.ecart_obj||0)>=0?"text-emerald-600":"text-rose-600"}`}>{fmtEur(d.ecart_obj)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-slate-500">{fmtEur(d.n_1)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-blue-700">{fmtEur(d.achats_est)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-rose-600">{fmtEur(d.casse_est)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums font-semibold">{fmtPct(d.foodcost_est,1)}</td>
                </tr>
              ))}
              {days.length === 0 && <tr><td colSpan={8} className="px-4 py-6 text-center italic text-slate-400">Aucune donnée journalière pour cette semaine.</td></tr>}
            </tbody>
          </table>
        </div>
        {data?.note && <div className="mt-2 text-[10px] italic text-slate-400">{data.note}</div>}
      </div>
    </>
  );
}

/* ─── Filter bar ─── */
function FilterBar({ meta, f, setF }) {
  const drOpts = useMemo(()=>(meta?.drs||[]).slice().sort(),[meta]);
  const secOpts = useMemo(()=>{
    const drs = f.drs||[]; const s=new Set();
    (meta?.corners||[]).forEach(c=>{ if(drs.length && !drs.includes(c.dr)) return; if(c.secteur) s.add(c.secteur); });
    return [...s].sort();
  },[meta,f.drs]);
  const cornerOpts = useMemo(()=>{
    const drs=f.drs||[], secs=f.secteurs||[]; const keys=[], labels={};
    (meta?.corners||[]).forEach(c=>{
      if (drs.length && !drs.includes(c.dr)) return;
      if (secs.length && !secs.includes(c.secteur)) return;
      if (!c.corner_key) return;
      keys.push(c.corner_key); labels[c.corner_key] = c.corner;
    });
    return { keys, labels };
  },[meta,f.drs,f.secteurs]);
  const moOpts = useMemo(()=>(meta?.mois||[]).slice().sort((a,b)=>MONTHS_FR.indexOf(a)-MONTHS_FR.indexOf(b)),[meta]);
  const wkOpts = useMemo(()=>(meta?.semaines||[]).map(String),[meta]);
  const yrOpts = meta?.annees||[];
  const nActive = (f.drs?.length||0)+(f.secteurs?.length||0)+(f.corners?.length||0)+(f.annees?.length||0)+(f.mois?.length||0)+(f.semaines?.length||0);
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-3 shadow-[0_1px_3px_rgba(15,23,42,.04)]">
      <div className="flex flex-wrap gap-2 items-end">
        <MultiSelect label="DR" testid="ach-f-dr" options={drOpts} values={f.drs||[]} onChange={x=>setF(p=>({...p,drs:x,secteurs:[],corners:[]}))}/>
        <MultiSelect label="Secteur" testid="ach-f-sec" options={secOpts} values={f.secteurs||[]} onChange={x=>setF(p=>({...p,secteurs:x,corners:[]}))}/>
        <MultiSelect label="Corner" testid="ach-f-corner" options={cornerOpts.keys} labelMap={cornerOpts.labels} values={f.corners||[]} onChange={x=>setF(p=>({...p,corners:x}))}/>
        <MultiSelect label="Année" testid="ach-f-year" options={yrOpts.map(String)} values={(f.annees||[]).map(String)} onChange={x=>setF(p=>({...p,annees:x.length?[Number(x[0])]:[]}))} single/>
        <MultiSelect label="Mois" testid="ach-f-mois" options={moOpts} values={f.mois||[]} onChange={x=>setF(p=>({...p,mois:x}))}/>
        <MultiSelect label="Semaine" testid="ach-f-wk" options={wkOpts} values={(f.semaines||[]).map(String)} onChange={x=>setF(p=>({...p,semaines:x.map(Number)}))}/>
        {nActive>0 && <button onClick={()=>setF({})} data-testid="ach-f-reset" className="ml-auto text-[11px] font-semibold text-slate-500 hover:text-slate-800 px-2 py-1 rounded hover:bg-slate-100">Réinitialiser ({nActive})</button>}
      </div>
    </div>
  );
}

/* ═══════════════════════ TAB : VUE SYNTHÈSE ═══════════════════════ */
function TabSynthese({ data, f, search, setSearch }) {
  const s = data.summary || {};
  const [fcEvolMode, setFcEvolMode] = useState("mois"); // mois | semaine
  const [weekDrawer, setWeekDrawer] = useState(null); // { semaine } | null
  const fcEvolData = useMemo(() => {
    if (fcEvolMode === "semaine") {
      return (data.weekly || []).map(w => ({
        label: `S${String(w.semaine).replace(/^S0?/, "").padStart(2,"0")}`,
        semaine: w.semaine,
        foodcost: w.foodcost || 0,
        obj_foodcost: w.obj_foodcost || 0.24,
        date_start: w.date_start, date_end: w.date_end,
      }));
    }
    return (data.evolution || []).map(m => ({ ...m, label: m.mois }));
  }, [fcEvolMode, data.evolution, data.weekly]);
  const rows = data.by_corner || [];
  const [sortKey, setSortKey] = useState("foodcost");
  const [sortDir, setSortDir] = useState("desc");
  const [statutFilter, setStatutFilter] = useState("all"); // all | conforme | surveiller | horsobj
  const toggleSort = (k) => {
    if (sortKey === k) {
      setSortDir(d => d === "asc" ? "desc" : "asc");
    } else {
      const isText = ["corner", "dr", "secteur", "bm", "fournisseur_principal", "statut"].includes(k);
      setSortKey(k);
      setSortDir(isText ? "asc" : "desc");
    }
  };
  // Comparateur explicite pour CHAQUE colonne (fix bug : anciennement seules certaines cols marchaient)
  const getSortVal = (c, k) => {
    switch (k) {
      case "corner":            return (c.corner || "").toLowerCase();
      case "dr":                return (c.dr || "").toLowerCase();
      case "secteur":           return (c.secteur || "").toLowerCase();
      case "bm":                return (c.bm || "").toLowerCase();
      case "fournisseur_principal": return (c.fournisseur_principal || "").toLowerCase();
      case "ca_ht":             return c.ca_ht || 0;
      case "budget":            return c.budget || 0;
      case "achats_ht":         return c.achats_ht || 0;
      case "reste_budget":      return c.reste_budget || 0;
      case "foodcost":          return c.foodcost || 0;
      case "obj_foodcost":      return c.obj_foodcost || 0;
      case "ecart_pts":         return c.ecart_pts != null ? c.ecart_pts : (c.foodcost || 0) - (c.obj_foodcost || 0);
      case "casse_pct":         return c.casse_pct || 0;
      case "kg_saumon":         return c.kg_saumon || 0;
      case "foodcost_saumon":   return c.foodcost_saumon || 0;
      case "statut": {
        const ok   = c.foodcost <= (c.obj_foodcost || 0.24) + 0.005;
        const warn = c.foodcost <= (c.obj_foodcost || 0.24) + 0.03;
        return ok ? 2 : warn ? 1 : 0;
      }
      default: return 0;
    }
  };
  const statutOfRow = (c) => {
    const ok   = c.foodcost <= (c.obj_foodcost || 0.24) + 0.005;
    const warn = c.foodcost <= (c.obj_foodcost || 0.24) + 0.03;
    return ok ? "conforme" : warn ? "surveiller" : "horsobj";
  };
  const statutCounts = useMemo(() => {
    const c = { all: rows.length, conforme: 0, surveiller: 0, horsobj: 0 };
    rows.forEach(r => { c[statutOfRow(r)] += 1; });
    return c;
  }, [rows]);
  const filtered = useMemo(() => {
    const q = (search || "").toLowerCase();
    let base = rows.filter(r => {
      if (q && !((r.corner || "").toLowerCase().includes(q) ||
                 (r.dr || "").toLowerCase().includes(q) ||
                 (r.secteur || "").toLowerCase().includes(q) ||
                 (r.bm || "").toLowerCase().includes(q) ||
                 (r.fournisseur_principal || "").toLowerCase().includes(q))) return false;
      if (statutFilter !== "all" && statutOfRow(r) !== statutFilter) return false;
      return true;
    });
    const dir = sortDir === "asc" ? 1 : -1;
    base.sort((a, b) => {
      const va = getSortVal(a, sortKey);
      const vb = getSortVal(b, sortKey);
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      if (typeof va === "string") return va.localeCompare(vb) * dir;
      return (va - vb) * dir;
    });
    return base;
  }, [rows, search, sortKey, sortDir, statutFilter]);
  const exportViewCsv = () => {
    const header = ["Corner","DR","Secteur","BM","CA HT","Budget","Achats","Reste","FoodCost","Obj.","Écart pts","Casse %","Kg saumon","FC Saumon","Fournisseur","Statut"];
    const lines = [header.join(";")];
    filtered.forEach(c => {
      lines.push([
        c.corner, c.dr || "", c.secteur || "", c.bm || "",
        Math.round(c.ca_ht || 0), Math.round(c.budget || 0), Math.round(c.achats_ht || 0), Math.round(c.reste_budget || 0),
        (c.foodcost * 100).toFixed(1) + "%", (c.obj_foodcost * 100).toFixed(0) + "%",
        ((c.ecart_pts || 0) * 100).toFixed(1) + " pts",
        (c.casse_pct * 100).toFixed(1) + "%",
        Math.round(c.kg_saumon || 0),
        (c.foodcost_saumon * 100).toFixed(1) + "%",
        c.fournisseur_principal || "",
        statutOfRow(c),
      ].join(";"));
    });
    const blob = new Blob(["\ufeff" + lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `achats_synthese_${new Date().toISOString().slice(0,10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
    toast.success(`Export CSV — ${filtered.length} lignes`);
  };
  const fcOk = s.foodcost <= s.obj_foodcost + 0.005;
  return (
    <div className="space-y-4">
      {/* 7 KPI cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
        <KpiCard icon={Percent} iconColor="text-blue-600" iconBg="bg-blue-50" label="FoodCost"
                 value={fmtPct(s.foodcost)} badge={fcOk?"Obj. atteint":undefined}
                 sub={`Objectif : ${fmtPct(s.obj_foodcost,0)}`} subTone={fcOk?"up":"down"} testid="ach-kpi-fc"/>
        <KpiCard icon={ShoppingCart} iconColor="text-emerald-600" iconBg="bg-emerald-50" label="Achats HT"
                 value={fmtEurShort(s.achats_ht)} {...deltaSub(s.achats_ht, data.prev_summary?.achats_ht, data.prev_label, { goodWhenDown: true })} testid="ach-kpi-achats"/>
        <KpiCard icon={ShoppingCart} iconColor="text-slate-600" iconBg="bg-slate-50" label="CA HT"
                 value={fmtEurShort(s.ca_ht)} {...deltaSub(s.ca_ht, data.prev_summary?.ca_ht, data.prev_label)} testid="ach-kpi-ca"/>
        <KpiCard icon={Package} iconColor="text-blue-600" iconBg="bg-blue-50" label="Budget achat"
                 value={fmtEurShort(s.budget_total)} progress={s.budget_used_pct||0} sub={fmtPct(s.budget_used_pct,0)} testid="ach-kpi-budget"/>
        <KpiCard icon={Package} iconColor="text-emerald-600" iconBg="bg-emerald-50" label="Budget restant"
                 value={fmtEurShort(s.budget_restant)} sub={`Soit ${fmtPct((s.budget_restant||0)/(s.budget_total||1),1)} du budget`} testid="ach-kpi-reste"/>
        <KpiCard icon={AlertTriangle} iconColor="text-amber-600" iconBg="bg-amber-50" label="Casse"
                 value={fmtPct(s.casse_pct)} sub={`Objectif : ${fmtPct(s.obj_casse,0)}`} subTone={s.casse_pct<=s.obj_casse?"up":"down"} testid="ach-kpi-casse"/>
        <KpiCard icon={Fish} iconColor="text-blue-600" iconBg="bg-blue-50" label="Saumon"
                 value={fmtPct(s.foodcost_saumon)}
                 sub={`${fmtNum(s.kg_saumon)} kg · ${fmtNum(s.cout_saumon_kg||0, 2)} €/kg`}
                 testid="ach-kpi-saumon"/>
      </div>

      {/* Row 2 : Budget vs Realisé + Evolution + Répartition + Position */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-3">
        {/* Budget vs réalisé */}
        <div className="bg-white rounded-xl border border-slate-200 p-4 lg:col-span-1">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-[13px] font-bold text-slate-900">Budget achat vs réalisé</h3>
            <button className="text-[10px] font-semibold text-blue-600 hover:underline">Détail →</button>
          </div>
          <div className="space-y-2 text-[12px]">
            <div className="flex items-center justify-between"><span className="text-slate-600">Budget prévu</span><span className="tabular-nums font-semibold text-slate-900">{fmtEur(s.budget_total)}</span></div>
            <div className="flex items-center justify-between"><span className="text-slate-600">Achats réalisés</span><span className="tabular-nums font-semibold text-slate-900">{fmtEur(s.achats_ht)}</span></div>
            <div className="flex items-center justify-between border-t border-slate-100 pt-2"><span className="text-emerald-700 font-semibold">Reste à dépenser</span><span className="tabular-nums font-bold text-emerald-700">{fmtEur(s.budget_restant)}</span></div>
            <div className="w-full h-2 rounded-full bg-slate-100 overflow-hidden mt-2">
              <div className="h-full rounded-full bg-emerald-500" style={{width:`${Math.min(100,(s.budget_used_pct||0)*100)}%`}}/>
            </div>
            <div className="text-right text-[10px] text-emerald-600 font-semibold">{fmtPct(s.budget_used_pct,0)}</div>
          </div>
          <div className="mt-4">
            <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-2">Reste par semaine</div>
            <div className="grid grid-cols-4 gap-1.5">
              {(data.weekly||[]).slice(-4).map(w => {
                const rest = Math.max(0, (w.budget||0) - (w.achats||0));
                return (
                  <div key={w.semaine} className="text-center bg-slate-50 rounded-lg px-1 py-1.5">
                    <div className="text-[9px] text-slate-500">{w.semaine}</div>
                    <div className="text-[11px] font-bold text-slate-900 tabular-nums">{fmtNum(rest,0)} €</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        {/* Évolution FoodCost */}
        <div className="bg-white rounded-xl border border-slate-200 p-4 lg:col-span-2">
          <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
            <h3 className="text-[13px] font-bold text-slate-900">Évolution du FoodCost</h3>
            <div className="flex items-center gap-3 flex-wrap">
              <div className="inline-flex items-center border border-slate-200 rounded overflow-hidden" data-testid="fc-evol-toggle">
                <button data-testid="fc-evol-mois" onClick={()=>setFcEvolMode("mois")}
                        className={`px-2.5 py-1 text-[11px] font-semibold ${fcEvolMode==="mois"?"bg-blue-600 text-white":"bg-white text-slate-600 hover:bg-slate-50"}`}>Mois</button>
                <button data-testid="fc-evol-semaine" onClick={()=>setFcEvolMode("semaine")}
                        className={`px-2.5 py-1 text-[11px] font-semibold ${fcEvolMode==="semaine"?"bg-blue-600 text-white":"bg-white text-slate-600 hover:bg-slate-50"}`}>Semaine</button>
              </div>
              <div className="flex items-center gap-3 text-[10px]">
                <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-blue-600"/>FoodCost</span>
                <span className="flex items-center gap-1"><span className="w-2.5 h-0.5 border-t border-dashed border-emerald-500"/>Objectif ({fmtPct(s.obj_foodcost,0)})</span>
              </div>
            </div>
          </div>
          <div className="h-56">
            <ResponsiveContainer>
              <LineChart data={fcEvolData} margin={{top:8,right:14,left:0,bottom:0}}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9"/>
                <XAxis dataKey="label" tick={{fontSize:10,fill:"#64748B"}} axisLine={false} tickLine={false}/>
                <YAxis domain={[0.18,0.28]} tickFormatter={v=>`${Math.round(v*100)}%`} tick={{fontSize:10,fill:"#64748B"}} axisLine={false} tickLine={false}/>
                <Tooltip formatter={v=>fmtPct(v,1)}
                         labelFormatter={(label, payload) => {
                           const p = payload && payload[0] && payload[0].payload;
                           if (fcEvolMode==="semaine" && p?.date_start) return `${label} · ${p.date_start} → ${p.date_end||"…"}`;
                           return label;
                         }}/>
                <ReferenceLine y={s.obj_foodcost||0.24} stroke="#10B981" strokeDasharray="4 4"/>
                <Line type="monotone" dataKey="foodcost" stroke="#2563EB" strokeWidth={2.5}
                      dot={fcEvolMode==="semaine" ? (props) => {
                        const { cx, cy, payload } = props;
                        return <circle cx={cx} cy={cy} r={4} fill="#2563EB" stroke="#fff" strokeWidth={1.5}
                                       style={{cursor:"pointer"}}
                                       data-testid={`fc-week-dot-${payload?.semaine}`}
                                       onClick={()=>setWeekDrawer({semaine: payload?.semaine})}/>;
                      } : {r:3,fill:"#2563EB"}}
                      activeDot={{r:6, style:{cursor:"pointer"},
                                  onClick: (e, p) => {
                                    if (fcEvolMode==="semaine" && p?.payload?.semaine) setWeekDrawer({semaine: p.payload.semaine});
                                  }}}/>
              </LineChart>
            </ResponsiveContainer>
          </div>
          {fcEvolMode==="semaine" && fcEvolData.length===0 && (
            <div className="text-center text-[11px] italic text-slate-400 py-2">Aucune donnée hebdo pour ces filtres. Sélectionne un mois pour voir ses semaines.</div>
          )}
          {fcEvolMode==="semaine" && fcEvolData.length>0 && (
            <div className="text-center text-[10px] italic text-slate-400 pt-1">💡 Clique un point-semaine pour voir le détail journalier</div>
          )}
        </div>
        {/* Répartition fournisseurs */}
        <div className="bg-white rounded-xl border border-slate-200 p-4 lg:col-span-1">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-[13px] font-bold text-slate-900">Répartition par fournisseur</h3>
            <button className="text-[10px] font-semibold text-blue-600 hover:underline">Voir le détail →</button>
          </div>
          <div className="h-40 relative">
            <ResponsiveContainer>
              <PieChart>
                <Pie data={data.fournisseurs||[]} dataKey="achats" innerRadius={40} outerRadius={60} paddingAngle={2}>
                  {(data.fournisseurs||[]).map((f,i) => <Cell key={i} fill={FOUR_COLORS[f.fournisseur]||"#94A3B8"}/>)}
                </Pie>
                <Tooltip formatter={v=>fmtEur(v)}/>
              </PieChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 grid place-items-center pointer-events-none">
              <div className="text-center">
                <div className="text-sm font-bold text-slate-900 tabular-nums">{fmtEurShort(s.achats_ht)}</div>
                <div className="text-[9px] text-slate-500">Achats HT</div>
              </div>
            </div>
          </div>
          <div className="space-y-1 text-[10px] mt-2 max-h-20 overflow-auto">
            {(data.fournisseurs||[]).map(f => (
              <div key={f.fournisseur} className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full shrink-0" style={{background:FOUR_COLORS[f.fournisseur]||"#94A3B8"}}/>
                <span className="flex-1 truncate text-slate-700">{f.fournisseur}</span>
                <span className="tabular-nums font-semibold text-slate-900">{fmtPct(f.pct,0)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Position gauge */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-3">
        <div className="bg-white rounded-xl border border-slate-200 p-4 lg:col-span-1">
          <h3 className="text-[13px] font-bold text-slate-900 mb-2">Position vs objectif</h3>
          <Gauge value={s.foodcost} target={s.obj_foodcost} min={0.20} max={0.28} label={`Objectif ${fmtPct(s.obj_foodcost,0)}`}/>
        </div>
        {/* Top 5 best */}
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-2 mb-3"><Trophy className="w-4 h-4 text-emerald-600"/><h3 className="text-[13px] font-bold text-slate-900">Top 5 corners – Meilleur FoodCost</h3></div>
          <div className="space-y-1.5">
            {(data.top5_best||[]).slice(0,5).map((c,i) => (
              <div key={c.corner_key} className="flex items-center gap-2 text-[11px]">
                <span className="w-4 text-slate-400 font-semibold">{i+1}</span>
                <span className="flex-1 truncate font-medium text-slate-800">{c.corner}</span>
                <span className="tabular-nums text-slate-700 font-semibold">{fmtPct(c.foodcost)}</span>
                <span className="tabular-nums text-emerald-600 font-bold w-16 text-right">↓ {fmtPts(c.ecart_pts)}</span>
              </div>
            ))}
          </div>
        </div>
        {/* Top 5 worst */}
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-2 mb-3"><AlertTriangle className="w-4 h-4 text-rose-600"/><h3 className="text-[13px] font-bold text-slate-900">Top 5 corners – À surveiller</h3></div>
          <div className="space-y-1.5">
            {(data.top5_worst||[]).slice(0,5).map((c,i) => (
              <div key={c.corner_key} className="flex items-center gap-2 text-[11px]">
                <span className="w-4 text-slate-400 font-semibold">{i+1}</span>
                <span className="flex-1 truncate font-medium text-slate-800">{c.corner}</span>
                <span className="tabular-nums text-slate-700 font-semibold">{fmtPct(c.foodcost)}</span>
                <span className="tabular-nums text-rose-600 font-bold w-16 text-right">↑ {fmtPts(c.ecart_pts)}</span>
              </div>
            ))}
          </div>
        </div>
        {/* IA */}
        <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl border border-purple-100 p-4">
          <div className="flex items-center gap-2 mb-2"><OrbitAI className="w-4 h-4 text-purple-600"/><h3 className="text-[13px] font-bold text-slate-900">Analyse IA – Synthèse</h3></div>
          <div className="space-y-2 text-[11px]">
            <div className="flex items-start gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 mt-0.5 shrink-0"/><div><div className="font-semibold text-slate-800">Le FoodCost {fcOk?"reste sous l'objectif":"dépasse de "+fmtPts(s.foodcost-s.obj_foodcost)}</div><div className="text-slate-500">{fcOk?"Bonne dynamique, continuez ainsi.":"Actions correctives nécessaires."}</div></div></div>
            <div className="flex items-start gap-2"><Info className="w-3.5 h-3.5 text-blue-600 mt-0.5 shrink-0"/><div><div className="font-semibold text-slate-800">{data.fournisseurs?.[0]?.fournisseur || "—"} = {fmtPct(data.fournisseurs?.[0]?.pct||0,0)} des achats</div><div className="text-slate-500">Principal fournisseur.</div></div></div>
            <div className="flex items-start gap-2"><AlertTriangle className="w-3.5 h-3.5 text-amber-600 mt-0.5 shrink-0"/><div><div className="font-semibold text-slate-800">{(data.top5_worst||[]).length} corners au-dessus de 28%</div><div className="text-slate-500">Nécessitent un focus rapide.</div></div></div>
            <div className="flex items-start gap-2"><Package className="w-3.5 h-3.5 text-blue-600 mt-0.5 shrink-0"/><div><div className="font-semibold text-slate-800">Budget restant : {fmtEur(s.budget_restant)}</div><div className="text-slate-500">Soit {fmtPct((s.budget_restant||0)/(s.budget_total||1),1)} du budget.</div></div></div>
          </div>
          <button className="mt-3 text-[11px] font-semibold text-purple-700 hover:underline flex items-center gap-1">Voir l'analyse détaillée <ArrowRight className="w-3 h-3"/></button>
        </div>
      </div>

      {/* Détail par corner — Design harmonisé (toolbar + tri bidirectionnel toutes colonnes) */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-[0_1px_3px_rgba(15,23,42,.04)]" data-testid="ach-detail-table">
        <KpiTableToolbar
          q={search} setQ={setSearch}
          searchTestid="ach-detail-search"
          placeholder="Rechercher un corner…"
          status={statutFilter} setStatus={setStatutFilter}
          statusOptions={[
            { k: "all",         label: "Tous",           count: statutCounts.all },
            { k: "conforme",    label: "Conforme",       count: statutCounts.conforme,    color: "text-emerald-700" },
            { k: "surveiller",  label: "À surveiller",   count: statutCounts.surveiller,  color: "text-amber-700" },
            { k: "horsobj",     label: "Hors objectif",  count: statutCounts.horsobj,     color: "text-rose-700" },
          ]}
          statusTestidPrefix="ach-status"
          onExport={exportViewCsv}
          exportTestid="ach-export-view"
          leftSlot={
            <div className="flex items-center gap-2 min-w-0">
              <h2 className="text-[13px] font-bold text-slate-900 whitespace-nowrap">Détail par corner</h2>
              <span className="text-[11px] text-slate-500 whitespace-nowrap">{filtered.length}</span>
            </div>
          }
        />
        <div className="overflow-x-auto max-h-[500px] scrollbar-thin">
          <table className="w-full text-[11px] min-w-[1400px]">
            <thead className="bg-slate-50 border-b border-slate-200 sticky top-0 z-[1]">
              <tr>
                {[
                  { k: "corner",             label: "Corner",    right: false },
                  { k: "dr",                 label: "DR",        right: false },
                  { k: "secteur",            label: "Secteur",   right: false },
                  { k: "bm",                 label: "BM",        right: false },
                  { k: "ca_ht",              label: "CA HT",     right: true  },
                  { k: "budget",             label: "Budget",    right: true  },
                  { k: "achats_ht",          label: "Achats",    right: true  },
                  { k: "reste_budget",       label: "Reste",     right: true  },
                  { k: "foodcost",           label: "FoodCost",  right: true  },
                  { k: "obj_foodcost",       label: "Obj.",      right: true  },
                  { k: "ecart_pts",          label: "Écart",     right: true  },
                  { k: "casse_pct",          label: "Casse",     right: true  },
                  { k: "kg_saumon",          label: "Kg sau.",   right: true  },
                  { k: "foodcost_saumon",    label: "FC Sau.",   right: true  },
                  { k: "fournisseur_principal", label: "Fourniss.", right: false },
                  { k: "statut",             label: "Statut",    right: false },
                ].map(col => (
                  <th key={col.k}
                      className={`px-2 py-2 select-none ${col.right ? "text-right" : ""}`}
                      data-testid={`ach-th-${col.k}`}>
                    <SortHeadCell k={col.k} label={col.label}
                                  align={col.right ? "right" : "left"}
                                  sortKey={sortKey} sortDir={sortDir}
                                  onSort={toggleSort} testid={`ach-sort-${col.k}`}/>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((c, idx) => {
                const ok = c.foodcost <= (c.obj_foodcost||0.24) + 0.005;
                const warn = c.foodcost <= (c.obj_foodcost||0.24) + 0.03;
                return (
                  <tr key={`${c.corner_key || c.corner}-${idx}`} className="border-b border-slate-100 hover:bg-slate-50/60">
                    <td className="px-2 py-1.5 font-semibold text-slate-900 whitespace-nowrap">
                      <div className="flex items-center gap-1.5">
                        <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${ok?"bg-emerald-500":warn?"bg-amber-500":"bg-rose-500"}`}/>
                        <span className="truncate max-w-[140px]">{c.corner}</span>
                      </div>
                    </td>
                    <td className="px-2 py-1.5 text-slate-600 whitespace-nowrap truncate max-w-[100px]">{c.dr||"—"}</td>
                    <td className="px-2 py-1.5 text-slate-600 whitespace-nowrap truncate max-w-[100px]">{c.secteur||"—"}</td>
                    <td className="px-2 py-1.5 text-slate-600 whitespace-nowrap truncate max-w-[90px]">{c.bm||"—"}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums whitespace-nowrap">{fmtEurShort(c.ca_ht)}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums whitespace-nowrap">{fmtEurShort(c.budget)}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums font-semibold whitespace-nowrap">{fmtEurShort(c.achats_ht)}</td>
                    <td className={`px-2 py-1.5 text-right tabular-nums font-semibold whitespace-nowrap ${c.reste_budget>0?"text-emerald-600":"text-rose-600"}`}>{fmtEurShort(c.reste_budget)}</td>
                    <td className={`px-2 py-1.5 text-right tabular-nums font-bold whitespace-nowrap ${ok?"text-emerald-600":warn?"text-amber-600":"text-rose-600"}`}>{fmtPct(c.foodcost)}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums text-slate-500 whitespace-nowrap">{fmtPct(c.obj_foodcost,0)}</td>
                    <td className={`px-2 py-1.5 text-right tabular-nums font-semibold whitespace-nowrap ${c.ecart_pts<=0?"text-emerald-600":"text-rose-600"}`}>{fmtPts(c.ecart_pts)}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums whitespace-nowrap">{fmtPct(c.casse_pct)}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums whitespace-nowrap">{fmtNum(c.kg_saumon)} kg</td>
                    <td className="px-2 py-1.5 text-right tabular-nums font-semibold text-emerald-700 whitespace-nowrap">{fmtPct(c.foodcost_saumon)}</td>
                    <td className="px-2 py-1.5 text-slate-700 whitespace-nowrap truncate max-w-[110px]">{c.fournisseur_principal||"—"}</td>
                    <td className="px-2 py-1.5 whitespace-nowrap">
                      <span className={`inline-flex items-center gap-1 text-[9px] font-semibold px-1.5 py-0.5 rounded-full whitespace-nowrap ${ok?"bg-emerald-100 text-emerald-700":warn?"bg-amber-100 text-amber-700":"bg-rose-100 text-rose-700"}`}>
                        {ok?<CheckCircle2 className="w-2.5 h-2.5"/>:<AlertTriangle className="w-2.5 h-2.5"/>}
                        {ok?"Conforme":warn?"Surveiller":"Hors obj."}
                      </span>
                    </td>
                  </tr>
                );
              })}
              {filtered.length===0 && <tr><td colSpan={16} className="px-4 py-8 text-center text-slate-400 italic">Aucun corner pour ces filtres.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
      {weekDrawer && <WeekDetailDrawer semaine={weekDrawer.semaine} f={f} onClose={()=>setWeekDrawer(null)}/>}
    </div>
  );
}

/* ═══════════════════════ TAB : VUE DÉTAILLÉE ═══════════════════════ */
function TabDetail({ data, search, setSearch }) {
  const [subView, setSubView] = useState("corner"); // global | corner | secteur | dr | fournisseur
  const [drawerCK, setDrawerCK] = useState(null);   // corner_key ouvert dans le volet
  const s = data.summary || {};
  const rows = data.by_corner || [];
  const fours = data.fournisseurs_detail || [];
  const pivot = data.four_corner_pivot || [];

  /* ── €/KG saumon = CA HT / KG saumon acheté (nouvelle formule) ── */
  const eurPerKgGlobal = (s.kg_saumon > 0) ? (s.ca_ht / s.kg_saumon) : 0;

  /* ── Row transforms: add €/KG saumon + foodcost saumon per corner ── */
  const cornerRows = useMemo(() => rows.map(c => ({
    ...c,
    eur_par_kg: (c.kg_saumon > 0) ? ((c.ca_ht || 0) / c.kg_saumon) : 0,
    foodcost_saumon: (c.ca_ht > 0) ? ((c.cout_saumon || 0) / c.ca_ht) : 0,
  })), [rows]);

  /* ── Aggregations for Global / DR / Secteur views ── */
  const aggregate = (keyFn, labelFn) => {
    const groups = new Map();
    for (const c of cornerRows) {
      const k = keyFn(c) || "—";
      if (!groups.has(k)) groups.set(k, {
        key: k, label: labelFn ? labelFn(c) : k,
        n_corners: 0, ca_ht: 0, achats_ht: 0, budget: 0, casse_eur: 0,
        kg_saumon: 0, cout_saumon: 0, obj_fc_num: 0, obj_fc_den: 0,
      });
      const g = groups.get(k);
      g.n_corners += 1;
      g.ca_ht += c.ca_ht || 0;
      g.achats_ht += c.achats_ht || 0;
      g.budget += c.budget || 0;
      g.casse_eur += c.casse_eur || 0;
      g.kg_saumon += c.kg_saumon || 0;
      g.cout_saumon += c.cout_saumon || 0;
      g.obj_fc_num += (c.obj_foodcost || 0.24) * (c.ca_ht || 0);
      g.obj_fc_den += c.ca_ht || 0;
    }
    return Array.from(groups.values()).map(g => {
      const foodcost = g.ca_ht ? g.achats_ht / g.ca_ht : 0;
      const obj_foodcost = g.obj_fc_den ? g.obj_fc_num / g.obj_fc_den : 0.24;
      return {
        ...g,
        label: g.label,
        foodcost, obj_foodcost,
        ecart_pts: foodcost - obj_foodcost,
        casse_pct: g.ca_ht ? g.casse_eur / g.ca_ht : 0,
        reste_budget: Math.max(0, g.budget - g.achats_ht),
        eur_par_kg: g.kg_saumon ? g.ca_ht / g.kg_saumon : 0,
        foodcost_saumon: g.ca_ht ? g.cout_saumon / g.ca_ht : 0,
      };
    }).sort((a, b) => b.ca_ht - a.ca_ht);
  };

  const globalRow = useMemo(() => aggregate(() => "Réseau global", () => "Réseau global"), [cornerRows]);
  const drRows = useMemo(() => aggregate(c => c.dr, c => c.dr), [cornerRows]);
  const secteurRows = useMemo(() => aggregate(c => c.secteur, c => c.secteur), [cornerRows]);

  const filteredByCorner = search ? cornerRows.filter(r => (r.corner||"").toLowerCase().includes(search.toLowerCase())) : cornerRows;

  /* ── Fournisseur pivot : rows = fournisseurs, columns = corners ── */
  const pivotFournisseur = useMemo(() => {
    // group pivot rows by fournisseur → { four: { corner_key: achats_ht } }
    const byFour = new Map();
    for (const p of pivot) {
      if (!byFour.has(p.fournisseur)) byFour.set(p.fournisseur, {});
      byFour.get(p.fournisseur)[p.corner_key] = (byFour.get(p.fournisseur)[p.corner_key] || 0) + (p.achats_ht || 0);
    }
    return byFour;
  }, [pivot]);

  return (
    <div className="space-y-4">
      {/* KPI row (contextual) */}
      {(subView === "corner" || subView === "global" || subView === "dr" || subView === "secteur") && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
          <KpiCard icon={Percent} iconColor="text-blue-600" iconBg="bg-blue-50" label="FoodCost global" value={fmtPct(s.foodcost)} sub={`Obj. ${fmtPct(s.obj_foodcost,0)}`} subTone={s.foodcost<=s.obj_foodcost?"up":"down"}/>
          <KpiCard icon={ShoppingCart} iconColor="text-emerald-600" iconBg="bg-emerald-50" label="Achats HT" value={fmtEurShort(s.achats_ht)} {...deltaSub(s.achats_ht, data.prev_summary?.achats_ht, data.prev_label, { goodWhenDown: true })}/>
          <KpiCard icon={Package} iconColor="text-blue-600" iconBg="bg-blue-50" label="Budget achat" value={fmtEurShort(s.budget_total)} progress={s.budget_used_pct||0} sub={fmtPct(s.budget_used_pct,0)}/>
          <KpiCard icon={Package} iconColor="text-emerald-600" iconBg="bg-emerald-50" label="Budget restant" value={fmtEurShort(s.budget_restant)} sub={`Soit ${fmtPct((s.budget_restant||0)/(s.budget_total||1),1)} du budget`}/>
          <KpiCard icon={Fish} iconColor="text-blue-600" iconBg="bg-blue-50" label="Saumon" value={`${fmtNum(s.kg_saumon)} kg`} {...deltaSub(s.kg_saumon, data.prev_summary?.kg_saumon, data.prev_label)}/>
          <KpiCard icon={Fish} iconColor="text-blue-600" iconBg="bg-blue-50" label="€/KG saumon" value={`${fmtNum(eurPerKgGlobal,2)} €/kg`} sub="CA HT / kg saumon acheté"/>
          <KpiCard icon={AlertTriangle} iconColor="text-rose-600" iconBg="bg-rose-50" label="Casse" value={fmtPct(s.casse_pct)} sub={`Obj. ${fmtPct(s.obj_casse,0)}`} subTone={s.casse_pct<=s.obj_casse?"up":"down"}/>
        </div>
      )}
      {subView === "fournisseur" && (
        <>
          <div className="mb-1">
            <h2 className="text-[15px] font-bold text-slate-900">Vue détaillée – Achats par fournisseur × corner</h2>
            <div className="text-[11px] text-slate-500">Chaque fournisseur affiche l'indicateur "Achats HT" ventilé par corner (pivot brut kpi_ha_mix)</div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            <KpiCard icon={ShoppingCart} iconColor="text-blue-600" iconBg="bg-blue-50" label="Achats HT total" value={fmtEurShort(s.achats_ht)}/>
            <KpiCard icon={Users} iconColor="text-purple-600" iconBg="bg-purple-50" label="Nombre de fournisseurs" value={fmtNum(pivotFournisseur.size)} sub="Actifs sur la période"/>
            <KpiCard icon={LayoutGrid} iconColor="text-blue-600" iconBg="bg-blue-50" label="Nombre de corners" value={fmtNum(cornerRows.length)}/>
            <KpiCard icon={Fish} iconColor="text-blue-600" iconBg="bg-blue-50" label="€/KG saumon" value={`${fmtNum(eurPerKgGlobal,2)} €/kg`} sub="CA HT / kg saumon"/>
            <KpiCard icon={Percent} iconColor="text-blue-600" iconBg="bg-blue-50" label="FoodCost global" value={fmtPct(s.foodcost)} sub={`Obj. ${fmtPct(s.obj_foodcost,0)}`}/>
          </div>
        </>
      )}

      {/* Table container with sub-tabs */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap">
            <button data-testid="ach-detail-sub-global" onClick={()=>setSubView("global")}
                    className={`flex items-center gap-1.5 text-[11px] font-semibold px-3 py-1.5 rounded-md border ${subView==="global"?"bg-blue-50 text-blue-700 border-blue-200":"hover:bg-slate-50 text-slate-600 border-transparent"}`}>
              <LayoutGrid className="w-3.5 h-3.5"/> Vue globale
            </button>
            <button data-testid="ach-detail-sub-corner" onClick={()=>setSubView("corner")}
                    className={`flex items-center gap-1.5 text-[11px] font-semibold px-3 py-1.5 rounded-md border ${subView==="corner"?"bg-blue-50 text-blue-700 border-blue-200":"hover:bg-slate-50 text-slate-600 border-transparent"}`}>
              <Store className="w-3.5 h-3.5"/> Vue par corner
            </button>
            <button data-testid="ach-detail-sub-dr" onClick={()=>setSubView("dr")}
                    className={`flex items-center gap-1.5 text-[11px] font-semibold px-3 py-1.5 rounded-md border ${subView==="dr"?"bg-blue-50 text-blue-700 border-blue-200":"hover:bg-slate-50 text-slate-600 border-transparent"}`}>
              <Users className="w-3.5 h-3.5"/> Vue par DR
            </button>
            <button data-testid="ach-detail-sub-secteur" onClick={()=>setSubView("secteur")}
                    className={`flex items-center gap-1.5 text-[11px] font-semibold px-3 py-1.5 rounded-md border ${subView==="secteur"?"bg-blue-50 text-blue-700 border-blue-200":"hover:bg-slate-50 text-slate-600 border-transparent"}`}>
              <LayoutGrid className="w-3.5 h-3.5"/> Vue par secteur
            </button>
            <button data-testid="ach-detail-sub-fournisseur" onClick={()=>setSubView("fournisseur")}
                    className={`flex items-center gap-1.5 text-[11px] font-semibold px-3 py-1.5 rounded-md border ${subView==="fournisseur"?"bg-blue-50 text-blue-700 border-blue-200":"hover:bg-slate-50 text-slate-600 border-transparent"}`}>
              <Truck className="w-3.5 h-3.5"/> Vue par fournisseur
            </button>
          </div>
          <div className="ml-auto relative">
            <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2 top-1/2 -translate-y-1/2"/>
            <input value={search} onChange={e=>setSearch(e.target.value)}
                   placeholder={`Rechercher…`}
                   className="pl-7 pr-6 py-1.5 rounded-md border border-slate-200 text-[11px] focus:outline-none focus:ring-2 focus:ring-blue-200 min-w-[220px]"/>
          </div>
        </div>
        {subView === "corner"   && <DetailCornerTable rows={filteredByCorner} s={s} onCornerClick={setDrawerCK}/>}
        {subView === "global"   && <DetailAggregateTable rows={globalRow} s={s} groupLabel="Réseau" testid="ach-detail-agg-global"/>}
        {subView === "dr"       && <DetailAggregateTable rows={search ? drRows.filter(r=>(r.label||"").toLowerCase().includes(search.toLowerCase())) : drRows} s={s} groupLabel="DR" testid="ach-detail-agg-dr"/>}
        {subView === "secteur"  && <DetailAggregateTable rows={search ? secteurRows.filter(r=>(r.label||"").toLowerCase().includes(search.toLowerCase())) : secteurRows} s={s} groupLabel="Secteur" testid="ach-detail-agg-secteur"/>}
        {subView === "fournisseur" && <DetailFournisseurPivotTable pivotFournisseur={pivotFournisseur} corners={cornerRows} search={search}/>}
      </div>

      {/* Bottom row: donut + evolution + IA (only for corner view) */}
      {subView === "corner" && (
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-[13px] font-bold text-slate-900">Répartition du budget achat par fournisseur</h3>
            <button className="text-[10px] font-semibold text-blue-600 hover:underline">Voir le détail →</button>
          </div>
          <div className="h-52 relative">
            <ResponsiveContainer>
              <PieChart>
                <Pie data={data.fournisseurs||[]} dataKey="achats" innerRadius={55} outerRadius={80} paddingAngle={2}>
                  {(data.fournisseurs||[]).map((f,i)=><Cell key={i} fill={FOUR_COLORS[f.fournisseur]||"#94A3B8"}/>)}
                </Pie>
                <Tooltip formatter={v=>fmtEur(v)}/>
              </PieChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 grid place-items-center pointer-events-none">
              <div className="text-center"><div className="text-lg font-bold text-slate-900 tabular-nums">{fmtEurShort(s.budget_total)}</div><div className="text-[10px] text-slate-500">Budget total</div></div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-[13px] font-bold text-slate-900">Évolution des achats vs budget</h3>
            <div className="flex items-center gap-3 text-[10px]">
              <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-blue-500"/>Achats</span>
              <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-amber-500"/>FC</span>
            </div>
          </div>
          <div className="h-52">
            <ResponsiveContainer>
              <ComposedChart data={data.evolution||[]}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9"/>
                <XAxis dataKey="mois" tick={{fontSize:10}}/>
                <YAxis yAxisId="l" tick={{fontSize:10}} tickFormatter={v=>Math.round(v).toLocaleString("fr-FR").replace(/,/g," ")} width={80}/>
                <YAxis yAxisId="r" orientation="right" tick={{fontSize:10}} tickFormatter={v=>`${Math.round(v*100)}%`}/>
                <Tooltip/>
                <Bar yAxisId="l" dataKey="achats" fill="#3B82F6" radius={[4,4,0,0]}/>
                <Line yAxisId="r" type="monotone" dataKey="foodcost" stroke="#F59E0B" strokeWidth={2.5} dot={{r:3}}/>
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl border border-purple-100 p-4">
          <div className="flex items-center gap-2 mb-2"><OrbitAI className="w-4 h-4 text-purple-600"/><h3 className="text-[13px] font-bold text-slate-900">Analyse IA – Détail</h3></div>
          <div className="space-y-2 text-[11px]">
            <div className="flex items-start gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 mt-0.5 shrink-0"/><div><div className="font-semibold text-slate-800">Le FoodCost global est {s.foodcost<=s.obj_foodcost?"sous":"au-dessus de"} l'objectif</div><div className="text-slate-500">{s.foodcost<=s.obj_foodcost?"Bonne maîtrise des achats ce mois-ci.":"Marges de manœuvre disponibles."}</div></div></div>
            <div className="flex items-start gap-2"><AlertTriangle className="w-3.5 h-3.5 text-amber-600 mt-0.5 shrink-0"/><div><div className="font-semibold text-slate-800">{(data.top5_worst||[]).length} corners dépassent 28% de FoodCost</div><div className="text-slate-500">Actions ciblées recommandées.</div></div></div>
            <div className="flex items-start gap-2"><Fish className="w-3.5 h-3.5 text-blue-600 mt-0.5 shrink-0"/><div><div className="font-semibold text-slate-800">€/KG saumon moyen : {fmtNum(eurPerKgGlobal,2)} €/kg</div><div className="text-slate-500">CA HT réalisé par kg de saumon acheté.</div></div></div>
          </div>
        </div>
      </div>
      )}

      {/* Side drawer : détail semaine par semaine du corner cliqué */}
      {drawerCK && <CornerWeeklyDrawer corner_key={drawerCK} onClose={()=>setDrawerCK(null)}/>}
    </div>
  );
}

/* ─── Sub-tables for Vue détaillée ─── */
function DetailCornerTable({ rows, s, onCornerClick }) {
  // €/KG saumon summary total
  const total_kg = rows.reduce((a,x)=>a+(x.kg_saumon||0),0);
  const total_ca = rows.reduce((a,x)=>a+(x.ca_ht||0),0);
  const eur_per_kg_total = total_kg ? total_ca / total_kg : 0;
  return (
    <div className="overflow-auto max-h-[560px] scrollbar-thin">
      <table className="w-full text-[11px]">
        <thead className="sticky top-0 z-10">
          <tr className="text-[10px] font-bold uppercase tracking-wider bg-white border-b border-slate-200">
            <th className="px-2 py-2 bg-white"></th>
            <th colSpan={3} className="px-2 py-2 text-center bg-blue-50 text-blue-700">Activité</th>
            <th colSpan={4} className="px-2 py-2 text-center bg-amber-50 text-amber-700">FoodCost</th>
            <th colSpan={2} className="px-2 py-2 text-center bg-rose-50 text-rose-700">Casse</th>
            <th colSpan={3} className="px-2 py-2 text-center bg-emerald-50 text-emerald-700">Saumon</th>
            <th className="px-2 py-2 bg-white"></th>
          </tr>
          <tr className="text-[10px] font-semibold tracking-wider text-slate-500 uppercase bg-slate-50 border-b border-slate-200">
            <th className="px-2 py-2 text-left">Corner ↓</th>
            <th className="px-2 py-2 text-right">CA HT</th>
            <th className="px-2 py-2 text-right">Budget</th>
            <th className="px-2 py-2 text-right">Achats</th>
            <th className="px-2 py-2 text-right">Reste</th>
            <th className="px-2 py-2 text-right">FoodCost</th>
            <th className="px-2 py-2 text-right">Obj.</th>
            <th className="px-2 py-2 text-right">Écart</th>
            <th className="px-2 py-2 text-right">Casse €</th>
            <th className="px-2 py-2 text-right">Casse %</th>
            <th className="px-2 py-2 text-right">Kg</th>
            <th className="px-2 py-2 text-right">€/KG saumon</th>
            <th className="px-2 py-2 text-right">FC Saumon</th>
            <th className="px-2 py-2">Statut</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(c => {
            const ok = c.foodcost <= (c.obj_foodcost||0.24) + 0.005;
            const warn = c.foodcost <= (c.obj_foodcost||0.24) + 0.03;
            return (
              <tr key={c.corner_key}
                  onClick={()=>onCornerClick && onCornerClick(c.corner_key)}
                  data-testid={`ach-detail-row-${c.corner_key}`}
                  className="border-b border-slate-100 hover:bg-blue-50/60 cursor-pointer transition-colors">
                <td className="px-2 py-1.5 font-semibold text-slate-900 flex items-center gap-1.5">
                  <span className={`w-1.5 h-1.5 rounded-full ${ok?"bg-emerald-500":warn?"bg-amber-500":"bg-rose-500"}`}/>{c.corner}
                </td>
                <td className="px-2 py-1.5 text-right tabular-nums bg-blue-50/30">{fmtEur(c.ca_ht)}</td>
                <td className="px-2 py-1.5 text-right tabular-nums bg-blue-50/30">{fmtEur(c.budget)}</td>
                <td className="px-2 py-1.5 text-right tabular-nums bg-blue-50/30 font-semibold">{fmtEur(c.achats_ht)}</td>
                <td className={`px-2 py-1.5 text-right tabular-nums bg-amber-50/30 font-semibold ${c.reste_budget>0?"text-emerald-600":"text-rose-600"}`}>{fmtEur(c.reste_budget)}</td>
                <td className={`px-2 py-1.5 text-right tabular-nums bg-amber-50/30 font-bold ${ok?"text-emerald-600":warn?"text-amber-600":"text-rose-600"}`}>{fmtPct(c.foodcost)}</td>
                <td className="px-2 py-1.5 text-right tabular-nums bg-amber-50/30 text-slate-500">{fmtPct(c.obj_foodcost,0)}</td>
                <td className={`px-2 py-1.5 text-right tabular-nums bg-amber-50/30 font-semibold ${c.ecart_pts<=0?"text-emerald-600":"text-rose-600"}`}>{fmtPts(c.ecart_pts)}</td>
                <td className="px-2 py-1.5 text-right tabular-nums bg-rose-50/30">{fmtEur(c.casse_eur)}</td>
                <td className="px-2 py-1.5 text-right tabular-nums bg-rose-50/30">{fmtPct(c.casse_pct)}</td>
                <td className="px-2 py-1.5 text-right tabular-nums bg-emerald-50/30">{fmtNum(c.kg_saumon)}</td>
                <td className="px-2 py-1.5 text-right tabular-nums bg-emerald-50/30 font-semibold">{fmtNum(c.eur_par_kg,2)} €/kg</td>
                <td className="px-2 py-1.5 text-right tabular-nums bg-emerald-50/30 font-bold text-emerald-700">{fmtPct(c.foodcost_saumon)}</td>
                <td className="px-2 py-1.5"><span className={`inline-flex items-center gap-1 text-[9px] font-semibold px-1.5 py-0.5 rounded-full ${ok?"bg-emerald-100 text-emerald-700":warn?"bg-amber-100 text-amber-700":"bg-rose-100 text-rose-700"}`}>{ok?"Conforme":warn?"À surveiller":"Hors objectif"}</span></td>
              </tr>
            );
          })}
          {rows.length===0 && <tr><td colSpan={14} className="px-4 py-8 text-center text-slate-400 italic">Aucun corner.</td></tr>}
        </tbody>
        <tfoot className="bg-slate-100 border-t-2 border-slate-300 sticky bottom-0">
          <tr className="text-[11px] font-bold text-slate-800">
            <td className="px-2 py-2">TOTAL ({rows.length} corners)</td>
            <td className="px-2 py-2 text-right tabular-nums">{fmtEur(total_ca)}</td>
            <td className="px-2 py-2 text-right tabular-nums">{fmtEur(s.budget_total)}</td>
            <td className="px-2 py-2 text-right tabular-nums">{fmtEur(s.achats_ht)}</td>
            <td className="px-2 py-2 text-right tabular-nums text-emerald-600">{fmtEur(s.budget_restant)}</td>
            <td className="px-2 py-2 text-right tabular-nums">{fmtPct(s.foodcost)}</td>
            <td className="px-2 py-2 text-right tabular-nums">{fmtPct(s.obj_foodcost,0)}</td>
            <td className="px-2 py-2 text-right tabular-nums">{fmtPts(s.foodcost-s.obj_foodcost)}</td>
            <td className="px-2 py-2 text-right tabular-nums" colSpan={2}>{fmtPct(s.casse_pct)}</td>
            <td className="px-2 py-2 text-right tabular-nums">{fmtNum(total_kg)}</td>
            <td className="px-2 py-2 text-right tabular-nums">{fmtNum(eur_per_kg_total,2)} €/kg</td>
            <td className="px-2 py-2 text-right tabular-nums font-bold text-emerald-700">{fmtPct(s.foodcost_saumon)}</td>
            <td/>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

/* Aggregated table (Global / DR / Secteur) — même format que corner */
function DetailAggregateTable({ rows, s, groupLabel, testid }) {
  const total_kg = rows.reduce((a,x)=>a+(x.kg_saumon||0),0);
  const total_ca = rows.reduce((a,x)=>a+(x.ca_ht||0),0);
  const eur_per_kg_total = total_kg ? total_ca / total_kg : 0;
  const total_achats = rows.reduce((a,x)=>a+(x.achats_ht||0),0);
  const total_budget = rows.reduce((a,x)=>a+(x.budget||0),0);
  const total_casse = rows.reduce((a,x)=>a+(x.casse_eur||0),0);
  return (
    <div className="overflow-auto max-h-[560px] scrollbar-thin" data-testid={testid}>
      <table className="w-full text-[11px]">
        <thead className="sticky top-0 z-10">
          <tr className="text-[10px] font-semibold tracking-wider text-slate-500 uppercase bg-slate-50 border-b border-slate-200">
            <th className="px-2 py-2 text-left">{groupLabel} ↓</th>
            <th className="px-2 py-2 text-right">Nb corners</th>
            <th className="px-2 py-2 text-right">CA HT</th>
            <th className="px-2 py-2 text-right">Budget</th>
            <th className="px-2 py-2 text-right">Achats</th>
            <th className="px-2 py-2 text-right">Reste</th>
            <th className="px-2 py-2 text-right">FoodCost</th>
            <th className="px-2 py-2 text-right">Obj.</th>
            <th className="px-2 py-2 text-right">Écart</th>
            <th className="px-2 py-2 text-right">Casse €</th>
            <th className="px-2 py-2 text-right">Casse %</th>
            <th className="px-2 py-2 text-right">Kg saumon</th>
            <th className="px-2 py-2 text-right">€/KG saumon</th>
            <th className="px-2 py-2 text-right">FC Saumon</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(r => {
            const ok = r.foodcost <= (r.obj_foodcost||0.24) + 0.005;
            const warn = r.foodcost <= (r.obj_foodcost||0.24) + 0.03;
            return (
              <tr key={r.key} className="border-b border-slate-100 hover:bg-slate-50/60">
                <td className="px-2 py-1.5 font-semibold text-slate-900 flex items-center gap-1.5">
                  <span className={`w-1.5 h-1.5 rounded-full ${ok?"bg-emerald-500":warn?"bg-amber-500":"bg-rose-500"}`}/>{r.label}
                </td>
                <td className="px-2 py-1.5 text-right tabular-nums">{fmtNum(r.n_corners)}</td>
                <td className="px-2 py-1.5 text-right tabular-nums">{fmtEur(r.ca_ht)}</td>
                <td className="px-2 py-1.5 text-right tabular-nums">{fmtEur(r.budget)}</td>
                <td className="px-2 py-1.5 text-right tabular-nums font-semibold">{fmtEur(r.achats_ht)}</td>
                <td className={`px-2 py-1.5 text-right tabular-nums font-semibold ${r.reste_budget>0?"text-emerald-600":"text-rose-600"}`}>{fmtEur(r.reste_budget)}</td>
                <td className={`px-2 py-1.5 text-right tabular-nums font-bold ${ok?"text-emerald-600":warn?"text-amber-600":"text-rose-600"}`}>{fmtPct(r.foodcost)}</td>
                <td className="px-2 py-1.5 text-right tabular-nums text-slate-500">{fmtPct(r.obj_foodcost,0)}</td>
                <td className={`px-2 py-1.5 text-right tabular-nums font-semibold ${r.ecart_pts<=0?"text-emerald-600":"text-rose-600"}`}>{fmtPts(r.ecart_pts)}</td>
                <td className="px-2 py-1.5 text-right tabular-nums">{fmtEur(r.casse_eur)}</td>
                <td className="px-2 py-1.5 text-right tabular-nums">{fmtPct(r.casse_pct)}</td>
                <td className="px-2 py-1.5 text-right tabular-nums">{fmtNum(r.kg_saumon)}</td>
                <td className="px-2 py-1.5 text-right tabular-nums font-semibold">{fmtNum(r.eur_par_kg,2)} €/kg</td>
                <td className="px-2 py-1.5 text-right tabular-nums font-bold text-emerald-700">{fmtPct(r.foodcost_saumon)}</td>
              </tr>
            );
          })}
          {rows.length===0 && <tr><td colSpan={14} className="px-4 py-8 text-center text-slate-400 italic">Aucune donnée.</td></tr>}
        </tbody>
        <tfoot className="bg-slate-100 border-t-2 border-slate-300 sticky bottom-0">
          <tr className="text-[11px] font-bold text-slate-800">
            <td className="px-2 py-2">TOTAL ({rows.length})</td>
            <td className="px-2 py-2 text-right tabular-nums">{fmtNum(rows.reduce((a,x)=>a+(x.n_corners||0),0))}</td>
            <td className="px-2 py-2 text-right tabular-nums">{fmtEur(total_ca)}</td>
            <td className="px-2 py-2 text-right tabular-nums">{fmtEur(total_budget)}</td>
            <td className="px-2 py-2 text-right tabular-nums">{fmtEur(total_achats)}</td>
            <td className="px-2 py-2 text-right tabular-nums text-emerald-600">{fmtEur(Math.max(0,total_budget-total_achats))}</td>
            <td className="px-2 py-2 text-right tabular-nums">{fmtPct(total_ca?total_achats/total_ca:0)}</td>
            <td className="px-2 py-2 text-right tabular-nums">{fmtPct(s.obj_foodcost,0)}</td>
            <td className="px-2 py-2 text-right tabular-nums">{fmtPts((total_ca?total_achats/total_ca:0)-(s.obj_foodcost||0.24))}</td>
            <td className="px-2 py-2 text-right tabular-nums">{fmtEur(total_casse)}</td>
            <td className="px-2 py-2 text-right tabular-nums">{fmtPct(total_ca?total_casse/total_ca:0)}</td>
            <td className="px-2 py-2 text-right tabular-nums">{fmtNum(total_kg)}</td>
            <td className="px-2 py-2 text-right tabular-nums">{fmtNum(eur_per_kg_total,2)} €/kg</td>
            <td className="px-2 py-2 text-right tabular-nums font-bold text-emerald-700">{fmtPct(total_ca?rows.reduce((a,x)=>a+(x.cout_saumon||0),0)/total_ca:0)}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

/* Fournisseur pivot table : rows = fournisseurs · columns = corners */
function DetailFournisseurPivotTable({ pivotFournisseur, corners, search }) {
  const cornerCols = useMemo(() => {
    const cs = search ? corners.filter(c => (c.corner||"").toLowerCase().includes(search.toLowerCase())) : corners;
    return cs.sort((a,b)=>(a.corner||"").localeCompare(b.corner||""));
  }, [corners, search]);

  const fournisseurs = useMemo(() => {
    return Array.from(pivotFournisseur.keys()).sort();
  }, [pivotFournisseur]);

  // Total per fournisseur & per corner
  const totalsByFour = new Map();
  const totalsByCorner = new Map();
  let grandTotal = 0;
  for (const f of fournisseurs) {
    const row = pivotFournisseur.get(f) || {};
    let t = 0;
    for (const c of cornerCols) {
      const v = row[c.corner_key] || 0;
      t += v;
      totalsByCorner.set(c.corner_key, (totalsByCorner.get(c.corner_key) || 0) + v);
    }
    totalsByFour.set(f, t);
    grandTotal += t;
  }

  return (
    <div className="overflow-auto max-h-[600px] scrollbar-thin" data-testid="ach-detail-four-pivot">
      <table className="text-[11px] border-collapse">
        <thead className="sticky top-0 z-10 bg-slate-50">
          <tr className="text-[10px] font-bold uppercase tracking-wider text-slate-500 border-b border-slate-200">
            <th className="px-3 py-2 text-left sticky left-0 bg-slate-50 z-20 min-w-[180px] border-r border-slate-200">Fournisseur / Indicateur</th>
            {cornerCols.map(c => (
              <th key={c.corner_key} className="px-2 py-2 text-right min-w-[110px] whitespace-nowrap border-r border-slate-100">
                <div className="truncate max-w-[130px] font-semibold text-slate-700" title={c.corner}>{c.corner}</div>
                <div className="text-[8px] text-slate-400 font-normal">{c.dr||"—"}</div>
              </th>
            ))}
            <th className="px-3 py-2 text-right bg-slate-100 min-w-[110px] font-bold">TOTAL</th>
          </tr>
        </thead>
        <tbody>
          {fournisseurs.map(f => {
            const row = pivotFournisseur.get(f) || {};
            const fTotal = totalsByFour.get(f) || 0;
            return (
              <>
                {/* Section header per fournisseur */}
                <tr key={`h-${f}`} className="bg-blue-50/40 border-t-2 border-blue-100">
                  <td className="px-3 py-2 sticky left-0 bg-blue-50/40 z-10 border-r border-slate-200">
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full" style={{background:FOUR_COLORS[f]||"#94A3B8"}}/>
                      <span className="font-bold text-slate-900">{f}</span>
                    </div>
                  </td>
                  <td colSpan={cornerCols.length + 1} className="px-3 py-2 text-[10px] text-slate-500 italic">
                    {Object.keys(row).length} corners actifs · Total : <span className="font-bold text-slate-900 not-italic">{fmtEur(fTotal)}</span>
                  </td>
                </tr>
                {/* Indicateur row : Achats HT */}
                <tr key={`r-${f}`} className="border-b border-slate-100 hover:bg-slate-50/60">
                  <td className="px-3 py-1.5 pl-8 sticky left-0 bg-white z-10 border-r border-slate-200 text-slate-600">
                    <span className="text-[10px]">Achats HT</span>
                  </td>
                  {cornerCols.map(c => {
                    const v = row[c.corner_key] || 0;
                    return (
                      <td key={c.corner_key} className={`px-2 py-1.5 text-right tabular-nums border-r border-slate-50 ${v > 0 ? "text-slate-900 font-semibold" : "text-slate-300"}`}>
                        {v > 0 ? fmtEurShort(v) : "—"}
                      </td>
                    );
                  })}
                  <td className="px-3 py-1.5 text-right tabular-nums bg-slate-50 font-bold text-slate-900">{fmtEurShort(fTotal)}</td>
                </tr>
              </>
            );
          })}
          {fournisseurs.length === 0 && (
            <tr><td colSpan={cornerCols.length + 2} className="px-4 py-8 text-center text-slate-400 italic">Aucun fournisseur — vérifiez l'import Mix Fournisseur.</td></tr>
          )}
        </tbody>
        <tfoot className="sticky bottom-0 bg-slate-100 border-t-2 border-slate-300">
          <tr className="text-[11px] font-bold text-slate-900">
            <td className="px-3 py-2 sticky left-0 bg-slate-100 z-10 border-r border-slate-200">TOTAL ({fournisseurs.length} fournisseurs)</td>
            {cornerCols.map(c => (
              <td key={c.corner_key} className="px-2 py-2 text-right tabular-nums border-r border-slate-200">
                {fmtEurShort(totalsByCorner.get(c.corner_key) || 0)}
              </td>
            ))}
            <td className="px-3 py-2 text-right tabular-nums bg-slate-200">{fmtEurShort(grandTotal)}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

/* Side drawer showing week-by-week detail for a corner */
function CornerWeeklyDrawer({ corner_key, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    api.get("/kpi/achat/corner-weekly", { params: { corner_key } })
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [corner_key]);
  const ctx = data?.context || {};
  const weekly = data?.weekly || [];
  const totals = data?.totals || {};
  return (
    <>
      <div className="fixed inset-0 bg-slate-900/40 z-40" onClick={onClose} data-testid="ach-corner-drawer-backdrop"/>
      <div className="fixed right-0 top-0 bottom-0 w-full max-w-[900px] bg-white shadow-2xl z-50 flex flex-col" data-testid="ach-corner-drawer">
        <div className="flex items-center gap-3 px-6 py-4 border-b border-slate-200 bg-gradient-to-r from-blue-50 to-purple-50">
          <div className="w-10 h-10 rounded-xl bg-blue-600 text-white grid place-items-center"><Store className="w-5 h-5"/></div>
          <div className="flex-1 min-w-0">
            <h2 className="text-[16px] font-bold text-slate-900 truncate">{ctx.corner || corner_key}</h2>
            <div className="text-[11px] text-slate-600 flex items-center gap-2 flex-wrap">
              {ctx.dr && <span className="px-1.5 py-0.5 rounded bg-white border border-slate-200">DR : {ctx.dr}</span>}
              {ctx.secteur && <span className="px-1.5 py-0.5 rounded bg-white border border-slate-200">Secteur : {ctx.secteur}</span>}
              {ctx.bm && <span className="px-1.5 py-0.5 rounded bg-white border border-slate-200">BM : {ctx.bm}</span>}
              <span className="text-slate-500">· Détail semaine par semaine</span>
            </div>
          </div>
          <button onClick={onClose} data-testid="ach-corner-drawer-close" className="w-8 h-8 rounded-lg hover:bg-white grid place-items-center text-slate-500">
            <XIcon className="w-4 h-4"/>
          </button>
        </div>
        <div className="flex-1 overflow-auto p-6 space-y-4">
          {loading && <div className="text-center text-sm text-slate-400 py-12">Chargement…</div>}
          {!loading && weekly.length === 0 && <div className="text-center text-sm text-slate-400 py-12 italic">Aucune donnée hebdo pour ce corner sur la période.</div>}
          {!loading && weekly.length > 0 && (
            <>
              {/* Top KPIs */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <div className="bg-blue-50 rounded-lg p-3">
                  <div className="text-[10px] font-semibold text-blue-700 uppercase tracking-wider">CA HT</div>
                  <div className="text-base font-bold text-slate-900 tabular-nums">{fmtEurShort(totals.ca_ht)}</div>
                </div>
                <div className="bg-emerald-50 rounded-lg p-3">
                  <div className="text-[10px] font-semibold text-emerald-700 uppercase tracking-wider">Achats HT</div>
                  <div className="text-base font-bold text-slate-900 tabular-nums">{fmtEurShort(totals.achats)}</div>
                </div>
                <div className="bg-amber-50 rounded-lg p-3">
                  <div className="text-[10px] font-semibold text-amber-700 uppercase tracking-wider">FoodCost</div>
                  <div className="text-base font-bold text-slate-900 tabular-nums">{fmtPct(totals.foodcost)}</div>
                </div>
                <div className="bg-slate-50 rounded-lg p-3">
                  <div className="text-[10px] font-semibold text-slate-700 uppercase tracking-wider">€/KG saumon</div>
                  <div className="text-base font-bold text-slate-900 tabular-nums">{fmtNum(totals.eur_par_kg,2)} €/kg</div>
                </div>
              </div>
              {/* Weekly evolution mini chart */}
              <div className="bg-white border border-slate-200 rounded-lg p-3">
                <div className="text-[11px] font-bold text-slate-700 uppercase tracking-wider mb-2">Évolution hebdo</div>
                <div className="h-40">
                  <ResponsiveContainer>
                    <ComposedChart data={weekly}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9"/>
                      <XAxis dataKey="semaine" tick={{fontSize:10}}/>
                      <YAxis yAxisId="l" tick={{fontSize:10}} tickFormatter={v=>Math.round(v).toLocaleString("fr-FR").replace(/,/g," ")} width={80}/>
                      <YAxis yAxisId="r" orientation="right" tick={{fontSize:10}} tickFormatter={v=>`${Math.round(v*100)}%`}/>
                      <Tooltip formatter={(v,n) => n==="FoodCost" ? fmtPct(v) : fmtEur(v)}/>
                      <Bar yAxisId="l" dataKey="achats" name="Achats" fill="#3B82F6" radius={[4,4,0,0]}/>
                      <Line yAxisId="r" type="monotone" dataKey="foodcost" name="FoodCost" stroke="#F59E0B" strokeWidth={2.5} dot={{r:3}}/>
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              </div>
              {/* Detail table */}
              <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                <table className="w-full text-[11px]">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr className="text-left text-[9px] font-bold uppercase tracking-wider text-slate-500">
                      <th className="px-2 py-2">Semaine</th>
                      <th className="px-2 py-2 text-right">CA HT</th>
                      <th className="px-2 py-2 text-right">Achats</th>
                      <th className="px-2 py-2 text-right">Budget</th>
                      <th className="px-2 py-2 text-right">Reste</th>
                      <th className="px-2 py-2 text-right">FoodCost</th>
                      <th className="px-2 py-2 text-right">Écart</th>
                      <th className="px-2 py-2 text-right">Casse %</th>
                      <th className="px-2 py-2 text-right">Kg saumon</th>
                      <th className="px-2 py-2 text-right">€/KG saumon</th>
                    </tr>
                  </thead>
                  <tbody>
                    {weekly.map(w => (
                      <tr key={w.semaine} className="border-b border-slate-100 hover:bg-slate-50/60">
                        <td className="px-2 py-1.5 font-semibold text-slate-900">{w.semaine}</td>
                        <td className="px-2 py-1.5 text-right tabular-nums">{fmtEur(w.ca_ht)}</td>
                        <td className="px-2 py-1.5 text-right tabular-nums font-semibold">{fmtEur(w.achats)}</td>
                        <td className="px-2 py-1.5 text-right tabular-nums">{fmtEur(w.budget)}</td>
                        <td className={`px-2 py-1.5 text-right tabular-nums font-semibold ${w.reste_budget>0?"text-emerald-600":"text-rose-600"}`}>{fmtEur(w.reste_budget)}</td>
                        <td className={`px-2 py-1.5 text-right tabular-nums font-bold ${w.foodcost<=w.obj_foodcost?"text-emerald-600":"text-rose-600"}`}>{fmtPct(w.foodcost)}</td>
                        <td className={`px-2 py-1.5 text-right tabular-nums font-semibold ${w.ecart_pts<=0?"text-emerald-600":"text-rose-600"}`}>{fmtPts(w.ecart_pts)}</td>
                        <td className="px-2 py-1.5 text-right tabular-nums">{fmtPct(w.casse_pct)}</td>
                        <td className="px-2 py-1.5 text-right tabular-nums">{fmtNum(w.kg_saumon)}</td>
                        <td className="px-2 py-1.5 text-right tabular-nums font-semibold">{fmtNum(w.eur_par_kg,2)} €/kg</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-slate-100 border-t-2 border-slate-300">
                    <tr className="text-[11px] font-bold text-slate-900">
                      <td className="px-2 py-2">TOTAL ({weekly.length})</td>
                      <td className="px-2 py-2 text-right tabular-nums">{fmtEur(totals.ca_ht)}</td>
                      <td className="px-2 py-2 text-right tabular-nums">{fmtEur(totals.achats)}</td>
                      <td className="px-2 py-2 text-right tabular-nums">{fmtEur(totals.budget)}</td>
                      <td className="px-2 py-2 text-right tabular-nums text-emerald-600">{fmtEur(totals.reste_budget)}</td>
                      <td className="px-2 py-2 text-right tabular-nums">{fmtPct(totals.foodcost)}</td>
                      <td/>
                      <td className="px-2 py-2 text-right tabular-nums">{fmtPct(totals.casse_pct)}</td>
                      <td className="px-2 py-2 text-right tabular-nums">{fmtNum(totals.kg_saumon)}</td>
                      <td className="px-2 py-2 text-right tabular-nums">{fmtNum(totals.eur_par_kg,2)} €/kg</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}

/* ═══════════════════════ TAB : FOURNISSEURS ═══════════════════════ */
function TabFour({ data, search, setSearch }) {
  const s = data.summary || {};
  const fours = data.fournisseurs_detail || [];
  const filtered = search ? fours.filter(x => (x.fournisseur||"").toLowerCase().includes(search.toLowerCase())) : fours;
  const totalF = fours.reduce((s2,x)=>s2+(x.achats_ht||0),0)||1;
  const principal = fours[0] || {};
  return (
    <div className="space-y-4">
      {/* KPI */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <KpiCard icon={ShoppingCart} iconColor="text-emerald-600" iconBg="bg-emerald-50" label="Achats HT total" value={fmtEurShort(s.achats_ht)} {...deltaSub(s.achats_ht, data.prev_summary?.achats_ht, data.prev_label, { goodWhenDown: true })}/>
        <KpiCard icon={Package} iconColor="text-blue-600" iconBg="bg-blue-50" label="Budget fournisseurs" value={fmtEurShort(s.budget_total)} progress={s.budget_used_pct||0} sub={fmtPct(s.budget_used_pct,0)}/>
        <KpiCard icon={Users} iconColor="text-purple-600" iconBg="bg-purple-50" label="Nombre de fournisseurs" value={fmtNum(fours.length)} sub="Actifs ce mois"/>
        <KpiCard icon={Trophy} iconColor="text-amber-600" iconBg="bg-amber-50" label="Fournisseur principal" value={<span className="text-[15px]">{principal.fournisseur||"—"}</span>} sub={`${fmtPct(principal.pct_achats,0)} des achats`}/>
        <KpiCard icon={Package} iconColor="text-rose-600" iconBg="bg-rose-50" label="Écart budget total" value={fmtEurShort(-(s.budget_total-s.achats_ht))} sub={s.budget_total ? `${fmtPct((s.achats_ht - s.budget_total) / s.budget_total, 1)} vs budget` : undefined} subTone={s.budget_total && s.achats_ht > s.budget_total ? "down" : "up"}/>
        <KpiCard icon={Percent} iconColor="text-blue-600" iconBg="bg-blue-50" label="FoodCost impact" value={fmtPct(s.foodcost)} {...deltaSub(s.foodcost, data.prev_summary?.foodcost, data.prev_label, { pt: true, goodWhenDown: true })}/>
      </div>

      {/* Row: donut / stacked evol / poids FC */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <h3 className="text-[13px] font-bold text-slate-900 mb-2">Répartition des achats par fournisseur</h3>
          <div className="h-52 relative">
            <ResponsiveContainer>
              <PieChart>
                <Pie data={data.fournisseurs||[]} dataKey="achats" innerRadius={55} outerRadius={80} paddingAngle={2}>
                  {(data.fournisseurs||[]).map((f,i)=><Cell key={i} fill={FOUR_COLORS[f.fournisseur]||"#94A3B8"}/>)}
                </Pie>
                <Tooltip formatter={v=>fmtEur(v)}/>
              </PieChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 grid place-items-center pointer-events-none">
              <div className="text-center"><div className="text-lg font-bold text-slate-900 tabular-nums">{fmtEurShort(s.achats_ht)}</div><div className="text-[10px] text-slate-500">Achats HT</div></div>
            </div>
          </div>
          <div className="space-y-1 text-[10px] mt-2">
            {(data.fournisseurs||[]).map(f => (
              <div key={f.fournisseur} className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full" style={{background:FOUR_COLORS[f.fournisseur]||"#94A3B8"}}/>
                <span className="flex-1 truncate text-slate-700">{f.fournisseur}</span>
                <span className="tabular-nums font-semibold text-slate-900">{fmtPct(f.pct,0)}</span>
                <span className="tabular-nums text-slate-500 w-14 text-right">{fmtEurShort(f.achats)}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-[13px] font-bold text-slate-900">Évolution des achats par fournisseur</h3>
            <span className="text-[10px] text-slate-500">6 derniers mois</span>
          </div>
          <div className="h-52">
            <ResponsiveContainer>
              <BarChart data={data.evolution||[]}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9"/>
                <XAxis dataKey="mois" tick={{fontSize:10}}/>
                <YAxis tick={{fontSize:10}} tickFormatter={v=>Math.round(v).toLocaleString("fr-FR").replace(/,/g," ")} width={80}/>
                <Tooltip formatter={v=>fmtEur(v)}/>
                <Bar dataKey="foodcost" fill="#3B82F6" radius={[4,4,0,0]}/>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-[13px] font-bold text-slate-900">Poids dans le FoodCost</h3>
            <div className="flex items-center gap-1 text-[10px]"><button className="px-2 py-0.5 rounded bg-blue-100 text-blue-700 font-semibold">% du FC</button><button className="px-2 py-0.5 rounded hover:bg-slate-100 text-slate-500">Impact (pts)</button></div>
          </div>
          <div className="space-y-2 text-[11px]">
            {(data.fournisseurs||[]).map(f => (
              <div key={f.fournisseur} className="flex items-center gap-2">
                <span className="w-24 truncate text-slate-700 font-medium">{f.fournisseur}</span>
                <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden"><div className="h-full rounded-full" style={{width:`${f.pct*100}%`,background:FOUR_COLORS[f.fournisseur]||"#94A3B8"}}/></div>
                <span className="tabular-nums font-semibold text-slate-900 w-12 text-right">{(f.pct*(s.foodcost||0)*100).toFixed(1).replace(".",",")} pt</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Detail table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-3">
          <h2 className="text-[14px] font-bold text-slate-900">Détail par fournisseur</h2>
          <div className="ml-auto relative">
            <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2 top-1/2 -translate-y-1/2"/>
            <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Rechercher un fournisseur…"
                   className="pl-7 pr-3 py-1.5 rounded-md border border-slate-200 text-[11px] focus:outline-none focus:ring-2 focus:ring-blue-200 min-w-[220px]"/>
          </div>
        </div>
        <div className="overflow-auto">
          <table className="w-full text-[12px]">
            <thead className="bg-slate-50 border-b border-slate-200 sticky top-0">
              <tr className="text-left text-[10px] font-semibold tracking-wider text-slate-500 uppercase">
                <th className="px-3 py-2">#</th>
                <th className="px-3 py-2">Fournisseur</th>
                <th className="px-3 py-2 text-right">Achats HT</th>
                <th className="px-3 py-2 text-right">% Achats</th>
                <th className="px-3 py-2 text-right">Budget</th>
                <th className="px-3 py-2 text-right">Écart budget</th>
                <th className="px-3 py-2 text-right">Évolution</th>
                <th className="px-3 py-2 text-right">FoodCost impact</th>
                <th className="px-3 py-2">Statut</th>
                <th className="px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((f,i) => {
                const ok = (f.achats_ht||0) <= (f.budget||0);
                return (
                  <tr key={f.fournisseur} className="border-b border-slate-100 hover:bg-slate-50/60">
                    <td className="px-3 py-2 font-semibold text-slate-500">{i+1}</td>
                    <td className="px-3 py-2 font-semibold text-slate-900 flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full" style={{background:FOUR_COLORS[f.fournisseur]||"#94A3B8"}}/>
                      {f.fournisseur}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums font-semibold">{fmtEur(f.achats_ht)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmtPct(f.pct_achats,1)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmtEur(f.budget)}</td>
                    <td className={`px-3 py-2 text-right tabular-nums font-semibold ${f.ecart_budget>=0?"text-emerald-600":"text-rose-600"}`}>{fmtEur(-f.ecart_budget)}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-emerald-600 font-semibold">↑ +8,6%</td>
                    <td className="px-3 py-2 text-right tabular-nums">{(f.pct_achats*(s.foodcost||0)*100).toFixed(1).replace(".",",")} pt</td>
                    <td className="px-3 py-2">
                      <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Actif</span>
                    </td>
                    <td className="px-3 py-2"><button className="text-[10px] font-semibold text-blue-600 hover:underline">Voir détail</button></td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot className="bg-slate-100 border-t-2 border-slate-300">
              <tr className="text-[11px] font-bold text-slate-800">
                <td className="px-3 py-2" colSpan={2}>TOTAL</td>
                <td className="px-3 py-2 text-right tabular-nums">{fmtEur(totalF)}</td>
                <td className="px-3 py-2 text-right tabular-nums">100%</td>
                <td className="px-3 py-2 text-right tabular-nums">{fmtEur(fours.reduce((s2,x)=>s2+(x.budget||0),0))}</td>
                <td/><td/><td className="px-3 py-2 text-right tabular-nums">{fmtPct(s.foodcost,1)}</td><td/><td/>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* Bottom row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-[13px] font-bold text-slate-900">Top 5 corners par fournisseur</h3>
            <span className="text-[11px] px-2 py-1 rounded border border-slate-200 bg-slate-50 font-semibold text-slate-700">{principal.fournisseur||"—"}</span>
          </div>
          <div className="space-y-1.5">
            {(data.top5_worst||[]).slice(0,5).map((c,i) => (
              <div key={c.corner_key} className="flex items-center gap-2 text-[11px]">
                <span className="w-4 text-slate-400 font-semibold">{i+1}</span>
                <span className="flex-1 truncate font-medium text-slate-800">{c.corner}</span>
                <span className="tabular-nums text-slate-700 font-semibold">{fmtPct(c.foodcost)}</span>
                <span className="tabular-nums text-rose-600 font-bold w-16 text-right">↑ {fmtPts(c.ecart_pts)}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <h3 className="text-[13px] font-bold text-slate-900 mb-2">Évolution du rendement saumon</h3>
          <div className="h-40">
            <ResponsiveContainer>
              <LineChart data={data.evolution||[]}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9"/>
                <XAxis dataKey="mois" tick={{fontSize:10}}/>
                <YAxis domain={[0.55,0.75]} tick={{fontSize:10}} tickFormatter={v=>`${Math.round(v*100)}%`}/>
                <Tooltip formatter={v=>fmtPct(v)}/>
                <Line type="monotone" dataKey="foodcost" stroke="#2563EB" strokeWidth={2.5} dot={{r:3}}/>
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="text-center mt-2"><span className="text-2xl font-bold text-slate-900">{fmtPct(s.rendement)}</span><div className="text-[10px] text-slate-500">Rendement moyen{(() => { const d = deltaText(s.rendement, data.prev_summary?.rendement, { pt: true }); return d && data.prev_label ? ` · ${d.text} ${data.prev_label}` : ""; })()}</div></div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <h3 className="text-[13px] font-bold text-slate-900 mb-3">Actions rapides</h3>
          <div className="space-y-2">
            {[
              {icon:Calculator,label:"Simuler une commande"},
              {icon:BarChart3,label:"Comparer les fournisseurs"},
              {icon:AlertTriangle,label:"Voir les écarts budget"},
              {icon:Download,label:"Exporter le rapport"},
            ].map(a => {
              const I = a.icon;
              return (
                <button key={a.label} className="w-full flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 hover:bg-slate-50 text-[12px] font-medium text-slate-700">
                  <I className="w-3.5 h-3.5 text-blue-600"/>{a.label}<ArrowRight className="w-3 h-3 ml-auto text-slate-400"/>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════ TAB : SIMULATEUR ═══════════════════════ */
function TabSimu({ meta, f, setF, data }) {
  const [periode, setPeriode] = useState("mois");   // 'mois' | 'sem' | 'custom'
  const [semSel, setSemSel] = useState(null);
  const [moisSel, setMoisSel] = useState(null);
  const [dateStart, setDateStart] = useState("");
  const [dateEnd, setDateEnd] = useState("");
  const semaines = data?.periodes?.semaines || [];
  const moisList = data?.periodes?.mois || [];
  const lastDate = data?.periodes?.last_date;
  useEffect(() => { if (!semSel && semaines.length) setSemSel(semaines[semaines.length - 1]); }, [semaines, semSel]);
  useEffect(() => { if (!moisSel && moisList.length) setMoisSel(moisList[moisList.length - 1]); }, [moisList, moisSel]);
  useEffect(() => { if (!dateEnd && lastDate) { const e = new Date(lastDate + "T00:00:00"); const s0 = new Date(e); s0.setDate(e.getDate() - 29); setDateEnd(lastDate); setDateStart(s0.toISOString().slice(0,10)); } }, [lastDate, dateEnd]);

  // Données réelles recalculées sur la période sélectionnée uniquement (base de la situation actuelle ET de la simulation)
  const [simData, setSimData] = useState(null);
  const periodParams = useMemo(() => {
    const p = toParams({ drs: f.drs, secteurs: f.secteurs, corners: f.corners, annees: f.annees });
    if (periode === "sem") { if (!semSel) return null; p.semaines = semSel; }
    else if (periode === "mois") { if (!moisSel) return null; p.mois = moisSel; }
    else { if (!dateStart || !dateEnd) return null; p.date_from = dateStart; p.date_to = dateEnd; delete p.annees; }
    return p;
  }, [f.drs, f.secteurs, f.corners, f.annees, periode, semSel, moisSel, dateStart, dateEnd]);
  useEffect(() => {
    if (!periodParams) return;
    let alive = true;
    api.get("/kpi/achat/v2/summary", { params: periodParams }).then(r => { if (alive) setSimData(r.data); }).catch(() => { if (alive) setSimData(null); });
    return () => { alive = false; };
  }, [periodParams]);
  const s = simData?.summary || {};
  const periodLabel = periode === "sem" ? `Semaine ${semSel || "—"}` : periode === "mois" ? (moisSel ? moisSel.charAt(0) + moisSel.slice(1).toLowerCase() : "—") : `Du ${dateStart} au ${dateEnd}`;

  const [caPrev, setCaPrev] = useState(0);
  const [cmdStd, setCmdStd] = useState(0);
  const [cmdExc, setCmdExc] = useState(0);
  const [casseSim, setCasseSim] = useState(0.10);

  // Réinitialise les hypothèses sur les données réelles de la période à chaque changement de période
  useEffect(() => {
    setCaPrev(s.ca_ht ? Math.round(s.ca_ht * 1.028) : 0);
    setCasseSim(s.casse_pct || 0.10);
    setCmdStd(0); setCmdExc(0);
  }, [s.ca_ht, s.casse_pct]);

  const nbJours = useMemo(() => {
    if (periode === "sem") return 7;
    if (periode === "custom" && dateStart && dateEnd) {
      const dS = new Date(dateStart), dE = new Date(dateEnd);
      return Math.max(1, Math.round((dE - dS)/86400000) + 1);
    }
    const wk = (simData?.weekly || []).reduce((a, w) => a + (w.n_days || 0), 0);
    return wk || 30;
  }, [periode, dateStart, dateEnd, simData]);

  const situation = {
    ca_ht: s.ca_ht, achats: s.achats_ht,
    foodcost: s.foodcost, obj: s.obj_foodcost || 0.24,
    budget_restant: s.budget_restant, budget_total: s.budget_total,
    casse_pct: s.casse_pct, obj_casse: s.obj_casse || 0.10,
  };

  const proj = useMemo(() => {
    // Achats réalisés (baseline) + commandes à venir
    const achatsRealises = s.achats_ht || 0;
    const newAchats = achatsRealises + cmdStd + cmdExc;
    // Casse impacte les achats "consommés" comme perte additionnelle
    const casseEur = (casseSim || 0) * (caPrev || 0);
    // FoodCost = (achats + casse) / CA
    const newFC = caPrev ? (newAchats + casseEur) / caPrev : 0;
    // Budget achat recalculé selon la prévision de CA (le budget suit le CA : plus de CA prévu ⇒ plus de budget)
    const budget_total = situation.ca_ht > 0 && situation.budget_total > 0
      ? situation.budget_total * ((caPrev || 0) / situation.ca_ht)
      : (caPrev || 0) * (situation.obj || 0);
    const budget_restant = Math.max(0, budget_total - newAchats);
    const budget_depasse = Math.max(0, newAchats - budget_total);
    return {
      ca: caPrev,
      achats_realises: achatsRealises,
      achats: newAchats,
      casse_eur: casseEur,
      foodcost: newFC,
      obj: situation.obj,
      budget_total, budget_restant, budget_depasse,
      budget_delta: budget_total - (situation.budget_total || 0),
      ecart_pts: newFC - situation.obj,
      ok: newFC <= situation.obj + 0.005,
    };
  }, [s.achats_ht, cmdStd, cmdExc, caPrev, casseSim, situation.obj, situation.budget_total, situation.ca_ht]);

  const scenarios = [
    { key:"actual", label:"Situation actuelle (sans action)", ca: s.ca_ht, achats: s.achats_ht, fc: s.foodcost, ecart: (s.foodcost||0)-situation.obj, budget: s.budget_restant, ok: (s.foodcost||0)<=situation.obj+0.005 },
    { key:"reco",   label:"Scénario recommandé (baisse casse)", ca: caPrev, achats: proj.achats*0.97, fc: proj.foodcost-0.01, ecart: proj.foodcost-0.01-situation.obj, budget: Math.max(0,proj.budget_total-proj.achats*0.97), ok: (proj.foodcost-0.01) <= situation.obj+0.005 },
    { key:"proj",   label:"Projection selon vos hypothèses", ca: proj.ca, achats: proj.achats, fc: proj.foodcost, ecart: proj.ecart_pts, budget: proj.budget_restant, ok: proj.ok },
  ];

  return (
    <div className="space-y-4">
      {/* Filter Row + Period selector */}
      <div className="bg-white rounded-xl border border-slate-200 p-3">
        <div className="flex flex-wrap gap-3 items-end">
          <MultiSelect label="DR" testid="simu-f-dr" options={(meta?.drs||[]).slice().sort()} values={f.drs||[]} onChange={x=>setF(p=>({...p,drs:x,secteurs:[],corners:[]}))}/>
          <MultiSelect label="Secteur" options={(meta?.corners||[]).map(c=>c.secteur).filter((v,i,a)=>v&&a.indexOf(v)===i).sort()} values={f.secteurs||[]} onChange={x=>setF(p=>({...p,secteurs:x,corners:[]}))}/>
          <MultiSelect label="Corner" options={(meta?.corners||[]).map(c=>c.corner_key)} labelMap={Object.fromEntries((meta?.corners||[]).map(c=>[c.corner_key,c.corner]))} values={f.corners||[]} onChange={x=>setF(p=>({...p,corners:x}))} single/>
          {/* Period sub-selector */}
          <div className="flex flex-col gap-1 min-w-[200px]">
            <label className="block text-[10px] font-semibold text-slate-500">Période d'estimation</label>
            <div className="flex items-center gap-1 bg-slate-50 rounded-lg p-0.5">
              {[["sem","Semaine"],["mois","Mois"],["custom","Personnalisée"]].map(([k,l]) => (
                <button key={k} onClick={()=>setPeriode(k)}
                        data-testid={`simu-periode-${k}`}
                        className={`flex-1 text-[10px] font-semibold px-2 py-1 rounded ${periode===k?"bg-white text-blue-700 shadow-sm border border-blue-200":"text-slate-500 hover:text-slate-700"}`}>{l}</button>
              ))}
            </div>
          </div>
          {periode === "sem" && (
            <div className="flex flex-col gap-1">
              <label className="text-[10px] font-semibold text-slate-500">Semaine</label>
              <select value={semSel || ""} onChange={e=>setSemSel(e.target.value)} data-testid="simu-sem-select"
                      className="px-2 py-1.5 rounded-lg border border-slate-200 text-[12px] focus:outline-none focus:ring-2 focus:ring-blue-200">
                {semaines.map(w => <option key={w} value={w}>{w}</option>)}
              </select>
            </div>
          )}
          {periode === "mois" && (
            <div className="flex flex-col gap-1">
              <label className="text-[10px] font-semibold text-slate-500">Mois</label>
              <select value={moisSel || ""} onChange={e=>setMoisSel(e.target.value)} data-testid="simu-mois-select"
                      className="px-2 py-1.5 rounded-lg border border-slate-200 text-[12px] focus:outline-none focus:ring-2 focus:ring-blue-200">
                {moisList.map(m => <option key={m} value={m}>{m.charAt(0) + m.slice(1).toLowerCase()}</option>)}
              </select>
            </div>
          )}
          {periode === "custom" && (
            <>
              <div className="flex flex-col gap-1">
                <label className="text-[10px] font-semibold text-slate-500">Du</label>
                <input type="date" value={dateStart} onChange={e=>setDateStart(e.target.value)} data-testid="simu-date-start"
                       className="px-2 py-1.5 rounded-lg border border-slate-200 text-[12px] tabular-nums focus:outline-none focus:ring-2 focus:ring-blue-200"/>
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-[10px] font-semibold text-slate-500">Au</label>
                <input type="date" value={dateEnd} onChange={e=>setDateEnd(e.target.value)} data-testid="simu-date-end"
                       className="px-2 py-1.5 rounded-lg border border-slate-200 text-[12px] tabular-nums focus:outline-none focus:ring-2 focus:ring-blue-200"/>
              </div>
            </>
          )}
          <div className="ml-auto text-right">
            <div className="text-[10px] text-slate-500">Estimation sur</div>
            <div className="text-[14px] font-bold text-blue-700 tabular-nums" data-testid="simu-nbjours">{nbJours} jour{nbJours>1?"s":""}</div>
            <div className="text-[10px] text-slate-500" data-testid="simu-period-label">{periodLabel}</div>
          </div>
        </div>
      </div>

      {/* 1. Situation actuelle */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <h2 className="text-[13px] font-bold text-slate-900 mb-1">1. Situation actuelle</h2>
        <div className="text-[10px] text-slate-500 mb-3">Réalisé sur la période sélectionnée ({periodLabel}) · Base de calcul du simulateur</div>
        {!simData && <div className="text-[11px] text-slate-400 italic mb-2">Chargement des données de la période…</div>}
        {simData && !s.ca_ht && <div className="text-[11px] text-amber-700 font-semibold mb-2" data-testid="simu-nodata">Aucune donnée d'achats sur cette période / ce périmètre.</div>}
        <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-6 gap-3">
          <div className="lg:col-span-4 grid grid-cols-2 md:grid-cols-4 gap-3">
            <KpiCard icon={ShoppingCart} iconBg="bg-blue-50" iconColor="text-blue-600" label="CA HT réalisé" value={fmtEurShort(situation.ca_ht)} sub={periodLabel} testid="simu-ca-realise"/>
            <KpiCard icon={ShoppingCart} iconBg="bg-emerald-50" iconColor="text-emerald-600" label="Achats déjà réalisés" value={fmtEurShort(situation.achats)} sub={`${fmtPct((situation.achats||0)/(situation.ca_ht||1),1)} du CA · base`} testid="simu-achats-realises"/>
            <KpiCard icon={AlertTriangle} iconBg="bg-rose-50" iconColor="text-rose-600" label="FoodCost actuel" value={fmtPct(situation.foodcost)} sub={`${fmtPts(situation.foodcost-situation.obj)} vs objectif`} subTone={situation.foodcost<=situation.obj?"up":"down"}/>
            <KpiCard icon={Package} iconBg="bg-emerald-50" iconColor="text-emerald-600" label="Budget restant" value={fmtEurShort(situation.budget_restant)} sub={situation.budget_restant>0?"Sous budget":"Budget dépassé"}/>
          </div>
          <div className="bg-slate-50 rounded-xl p-3 border border-slate-100 lg:col-span-1">
            <div className="text-[11px] font-bold text-slate-900 mb-1">Position vs objectif</div>
            <Gauge value={situation.foodcost} target={situation.obj} min={0.20} max={0.35}/>
          </div>
          <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl border border-purple-100 p-4 lg:col-span-1">
            <div className="flex items-center gap-2 mb-2"><OrbitAI className="w-4 h-4 text-purple-600"/><h3 className="text-[13px] font-bold text-slate-900">Analyse IA</h3></div>
            <div className="text-[10px] text-slate-700 space-y-1">
              <div>Le FoodCost est {situation.foodcost<=situation.obj?"aligné avec":"au-dessus de"} l'objectif de {fmtPts(situation.foodcost-situation.obj)}.</div>
              <div className="text-slate-500">Pour revenir sous {fmtPct(situation.obj,0)}, réduire les achats d'environ {fmtEur(Math.max(0,situation.achats - situation.obj*situation.ca_ht))}.</div>
              <div className="text-slate-500">Casse actuelle : {fmtPct(situation.casse_pct,1)} · {situation.casse_pct>0.10?"au-dessus":"sous"} l'objectif 10%.</div>
            </div>
          </div>
        </div>
      </div>

      {/* 2. Paramètres */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <h2 className="text-[13px] font-bold text-slate-900 mb-1">2. Paramètres de simulation</h2>
        <div className="text-[10px] text-slate-500 mb-3">Ajustez vos hypothèses pour la période sélectionnée ({periodLabel} · {nbJours} jour{nbJours>1?"s":""}) — base : achats réalisés {fmtEurShort(situation.achats)} sur cette période</div>
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-3">
          <div className="bg-slate-50 rounded-lg p-3">
            <div className="flex items-center gap-2 mb-2"><ShoppingCart className="w-3.5 h-3.5 text-blue-600"/><span className="text-[11px] font-bold text-slate-800">Prévision CA</span></div>
            <label className="text-[10px] text-slate-500">CA prévisionnel sur la période</label>
            <input type="number" value={caPrev} onChange={e=>setCaPrev(Number(e.target.value)||0)}
                   className="w-full mt-1 px-2 py-1.5 rounded border border-slate-200 text-sm tabular-nums" data-testid="simu-ca-prev"/>
            <div className="text-[9px] text-emerald-600 font-semibold mt-1">{caPrev && s.ca_ht ? `${fmtPct((caPrev/s.ca_ht)-1,1)} vs réalisé sur la période` : ""}</div>
          </div>
          <div className="bg-slate-50 rounded-lg p-3">
            <div className="flex items-center gap-2 mb-2"><Package className="w-3.5 h-3.5 text-emerald-600"/><span className="text-[11px] font-bold text-slate-800">Achats à venir</span></div>
            <label className="text-[10px] text-slate-500">Commande standard</label>
            <input type="number" value={cmdStd} onChange={e=>setCmdStd(Number(e.target.value)||0)} className="w-full mt-1 px-2 py-1.5 rounded border border-slate-200 text-sm tabular-nums" data-testid="simu-cmd-std"/>
            <label className="text-[10px] text-slate-500 mt-2 block">Commande exceptionnelle</label>
            <input type="number" value={cmdExc} onChange={e=>setCmdExc(Number(e.target.value)||0)} className="w-full mt-1 px-2 py-1.5 rounded border border-slate-200 text-sm tabular-nums" data-testid="simu-cmd-exc"/>
            <div className="text-[9px] text-slate-500 mt-1">+ {fmtEur(situation.achats)} <span className="italic">déjà réalisés sur la période</span></div>
          </div>
          <div className="bg-slate-50 rounded-lg p-3">
            <div className="flex items-center gap-2 mb-2"><AlertTriangle className="w-3.5 h-3.5 text-amber-600"/><span className="text-[11px] font-bold text-slate-800">Casse estimée</span></div>
            <label className="text-[10px] text-slate-500 flex items-center justify-between">Taux de casse <span className="tabular-nums font-bold text-slate-800">{fmtPct(casseSim,1)}</span></label>
            <input type="range" min={0.03} max={0.20} step={0.001} value={casseSim} onChange={e=>setCasseSim(Number(e.target.value))} className="w-full accent-blue-600 mt-1" data-testid="simu-casse"/>
            <div className="flex items-center justify-between text-[9px] text-slate-500 tabular-nums"><span>3%</span><span className="text-emerald-600">Obj. 10%</span><span>20%</span></div>
            <div className="mt-2 rounded bg-white border border-slate-200 p-2 text-[10px]">
              <div className="text-slate-600">Impact en € (sur CA prévu):</div>
              <div className="text-[13px] font-bold text-rose-600 tabular-nums">{fmtEur(proj.casse_eur)}</div>
              <div className="text-slate-500">Impact FoodCost : <span className="font-bold text-slate-800">{((casseSim - situation.obj_casse)*100).toFixed(1).replace(".",",")} pt</span> vs objectif casse</div>
            </div>
          </div>
          <div className="bg-slate-50 rounded-lg p-3">
            <div className="text-[11px] font-bold text-slate-800 mb-2">Scénarios suggérés</div>
            <div className="space-y-2">
              {[
                {icon:CheckCircle2, tone:"emerald", label:"Baisser la casse à 8%", desc:"Focus DLC + rotation", action:()=>setCasseSim(0.08)},
                {icon:Target, tone:"amber", label:"Maintenir le plan", desc:"Aucun ajustement", action:()=>{ setCasseSim(situation.casse_pct); setCmdStd(0); setCmdExc(0); }},
                {icon:TrendingUp, tone:"blue", label:"Boost CA (+12 000 €)", desc:"Ajoute 12 000 € au CA prévu", action:()=>setCaPrev(v=>v+12000)},
              ].map(sc => {
                const I = sc.icon;
                return (
                  <button key={sc.label} onClick={sc.action} className="w-full bg-white rounded-lg border border-slate-200 hover:border-blue-300 p-2 flex items-center gap-2 text-left">
                    <I className={`w-3.5 h-3.5 text-${sc.tone}-600 shrink-0`}/>
                    <div className="flex-1 min-w-0">
                      <div className="text-[11px] font-semibold text-slate-800 truncate">{sc.label}</div>
                      <div className="text-[9px] text-slate-500 truncate">{sc.desc}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* 3. Résultat */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <h2 className="text-[13px] font-bold text-slate-900 mb-1">3. Résultat de la simulation</h2>
        <div className="text-[10px] text-slate-500 mb-3">
          Formule : (Achats réalisés {fmtEurShort(situation.achats)} + Commandes {fmtEurShort(cmdStd+cmdExc)} + Casse {fmtEurShort(proj.casse_eur)}) / CA prévu {fmtEurShort(proj.ca)}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
          <div className="lg:col-span-2 grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className={`rounded-xl border p-3 ${proj.ok?"bg-emerald-50 border-emerald-200":"bg-rose-50 border-rose-200"}`} data-testid="simu-result-fc">
              <div className="text-[10px] font-semibold uppercase text-slate-500 tracking-wider">FoodCost projeté</div>
              <div className={`text-2xl font-bold tabular-nums ${proj.ok?"text-emerald-700":"text-rose-700"}`}>{fmtPct(proj.foodcost)}</div>
              <div className="text-[10px] text-slate-500">Objectif : {fmtPct(proj.obj,0)}</div>
              <button className={`mt-2 text-[11px] font-semibold px-2 py-1 rounded ${proj.ok?"bg-emerald-100 text-emerald-700":"bg-rose-100 text-rose-700"}`}>{proj.ok?"Dans l'objectif":"Hors objectif"}</button>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-3">
              <div className="text-[10px] font-semibold uppercase text-slate-500 tracking-wider">Achats totaux projetés</div>
              <div className="text-2xl font-bold text-slate-900 tabular-nums">{fmtEurShort(proj.achats)}</div>
              <div className="text-[10px] text-slate-500">dont {fmtEurShort(proj.achats_realises)} déjà réalisés</div>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-3">
              <div className="text-[10px] font-semibold uppercase text-slate-500 tracking-wider">Budget restant</div>
              <div className={`text-2xl font-bold tabular-nums ${proj.budget_depasse>0?"text-rose-700":"text-slate-900"}`}>
                {proj.budget_depasse>0 ? `-${fmtEurShort(proj.budget_depasse)}` : fmtEurShort(proj.budget_restant)}
              </div>
              <div className="text-[10px] text-slate-500" data-testid="sim-budget-restant-sub">
                {proj.budget_depasse>0 ? "Budget dépassé" : `Soit ${fmtPct(proj.budget_restant/(proj.budget_total||1),1)} du budget`}
                {" · "}Budget projeté {fmtEurShort(proj.budget_total)}
                {Math.abs(proj.budget_delta) >= 1 && <span className={proj.budget_delta>0?"text-emerald-600 font-semibold":"text-rose-600 font-semibold"}> ({proj.budget_delta>0?"+":"-"}{fmtEurShort(Math.abs(proj.budget_delta))} vs budget actuel)</span>}
              </div>
              <button className={`mt-2 text-[11px] font-semibold px-2 py-1 rounded ${proj.budget_depasse>0?"bg-rose-100 text-rose-700":"bg-emerald-100 text-emerald-700"}`}>{proj.budget_depasse>0?"Dépassement":"Budget sécurisé"}</button>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-3">
              <div className="text-[10px] font-semibold uppercase text-slate-500 tracking-wider">Casse projetée</div>
              <div className="text-2xl font-bold text-slate-900 tabular-nums">{fmtEurShort(proj.casse_eur)}</div>
              <div className="text-[10px] text-slate-500">Soit {fmtPct(casseSim,1)} du CA</div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-3">
            <div className="flex items-center justify-between mb-1">
              <h3 className="text-[12px] font-bold text-slate-900">Évolution du FoodCost (projection)</h3>
              <div className="flex items-center gap-2 text-[9px]">
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-blue-600"/>Actuel</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-purple-600"/>Projection</span>
              </div>
            </div>
            <div className="h-32">
              <ResponsiveContainer>
                <LineChart data={(simData?.evolution||[]).concat([{mois:"Proj.",foodcost:proj.foodcost}])}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9"/>
                  <XAxis dataKey="mois" tick={{fontSize:9}}/>
                  <YAxis domain={[0.18,0.35]} tick={{fontSize:9}} tickFormatter={v=>`${Math.round(v*100)}%`}/>
                  <Tooltip formatter={v=>fmtPct(v,1)}/>
                  <ReferenceLine y={situation.obj} stroke="#10B981" strokeDasharray="4 4"/>
                  <Line type="monotone" dataKey="foodcost" stroke="#2563EB" strokeWidth={2}/>
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div className="text-right text-[10px] font-semibold text-slate-500">Projection sur {nbJours} j : {fmtPct(proj.foodcost,1)}</div>
          </div>
        </div>
      </div>

      {/* 4. Comparatif + Prochaines actions */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 p-4">
          <h2 className="text-[13px] font-bold text-slate-900 mb-3">Comparatif des scénarios</h2>
          <table className="w-full text-[11px]">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-[10px] font-semibold tracking-wider text-slate-500 uppercase">
                <th className="px-2 py-2">Scénario</th>
                <th className="px-2 py-2 text-right">CA projeté</th>
                <th className="px-2 py-2 text-right">Achats</th>
                <th className="px-2 py-2 text-right">FoodCost</th>
                <th className="px-2 py-2 text-right">Écart</th>
                <th className="px-2 py-2 text-right">Budget restant</th>
                <th className="px-2 py-2">Statut</th>
              </tr>
            </thead>
            <tbody>
              {scenarios.map(sc => (
                <tr key={sc.key} className={`border-b border-slate-100 ${sc.key==="proj"?"bg-blue-50/40":""}`}>
                  <td className="px-2 py-2 flex items-center gap-2"><input type="radio" name="sc" defaultChecked={sc.key==="proj"} className="accent-blue-600"/><span className="font-semibold text-slate-800">{sc.label}</span></td>
                  <td className="px-2 py-2 text-right tabular-nums">{fmtEurShort(sc.ca)}</td>
                  <td className="px-2 py-2 text-right tabular-nums">{fmtEurShort(sc.achats)}</td>
                  <td className="px-2 py-2 text-right tabular-nums font-bold">{fmtPct(sc.fc,1)}</td>
                  <td className={`px-2 py-2 text-right tabular-nums font-semibold ${sc.ecart<=0.005?"text-emerald-600":"text-rose-600"}`}>{fmtPts(sc.ecart)}</td>
                  <td className={`px-2 py-2 text-right tabular-nums ${sc.budget>=0?"text-emerald-600":"text-rose-600"}`}>{fmtEurShort(sc.budget)}</td>
                  <td className="px-2 py-2"><span className={`text-[9px] font-semibold px-2 py-0.5 rounded-full ${sc.ok?"bg-emerald-100 text-emerald-700":"bg-rose-100 text-rose-700"}`}>{sc.ok?"Dans l'objectif":"Hors objectif"}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <h3 className="text-[13px] font-bold text-slate-900 mb-3">Prochaines actions</h3>
          <div className="space-y-2">
            {[
              {icon:AlertTriangle, tone:"rose", title:`Réduire les commandes (-${fmtEur(Math.max(0,proj.achats - situation.obj*proj.ca))})`, desc:"Pour rentrer dans l'objectif", cta:"Créer une commande"},
              {icon:Package, tone:"blue", title:"Suivre les réceptions", desc:"Contrôler volumes et casse", cta:"Voir les achats"},
              {icon:Fish, tone:"emerald", title:"Baisser la casse à 8%", desc:"Focus DLC + rotation stock", cta:"Plan d'action"},
            ].map(a => {
              const I = a.icon;
              return (
                <div key={a.title} className="border border-slate-200 rounded-lg p-2 flex items-center gap-2">
                  <input type="checkbox" className="accent-blue-600"/>
                  <I className={`w-3.5 h-3.5 text-${a.tone}-600 shrink-0`}/>
                  <div className="flex-1 min-w-0"><div className="text-[11px] font-semibold text-slate-800 truncate">{a.title}</div><div className="text-[9px] text-slate-500 truncate">{a.desc}</div></div>
                  <button className={`text-[10px] font-semibold text-${a.tone}-700 hover:underline shrink-0`}>{a.cta}</button>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
